import React, { useState, useEffect, useRef } from 'react';
import { visibleCaseQueries } from '../data/disciplineQueries'; import DisciplineOfficerNotes from '../components/DisciplineOfficerNotes'; import { writesDisciplineOfficerNotes } from '../data/disciplineOfficerNotes';   // ruling 56 (Record 577); Record 637 (305.9): the officer notes card, imported on this line so no cited line moves
import { db, storage, auth } from '../firebase';
import {
  collection, addDoc, doc, getDoc, setDoc, updateDoc,
  serverTimestamp, query, where, orderBy, onSnapshot, arrayUnion
} from 'firebase/firestore';
import { EVENT_KINDS, planHearingEvent, hearingTimeline, PRESERVATION_CLASSES, PRESERVATION_OUTCOMES, decisionClock, transcriptClock, ORAL_ACT_WARNING } from '../data/hearingEvents';   // 6a-08 (section 247): the hearing's dated ACTS; ruling 158: the preservation demand
import { ref, uploadBytes } from 'firebase/storage';
import { viewStoredFile } from '../data/scannedFile';   // Record 591 (ruling 247): View fetches the link when clicked, after the scan
import { COMMITTEE_LOOKUP, AGREEMENT_LOOKUP, RAILROADS, LOCATIONS_BY_RAILROAD } from '../data/agreementData';
import RulingRef from '../components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { PARENT_ORGANIZATION } from '../data/letterGroup';   // ruling 188: the parent International heads every document
import { disciplineObjectTag } from '../data/disciplineStorage';
import DisciplineLettersPanel, { isLetterOfficer } from '../components/DisciplineLettersPanel';   // 10.16: the Organization's letters   // rulings 187/190: who may read a case's files
import { resolveScopeKey } from '../data/scopeKeyData';  // ruling 100: the LC's territory key, the same one claims carry
import { screenDeadlines, disciplineLimits } from '../data/disciplineAnswerLimits'; import HowToPanel from '../components/HowToPanel';   // section 442: the discipline clocks, from the record; Bob 2026-10-15: the step-by-step how-to, imported on this line so no cited line moves
import { adminReadDraft, shouldTryAuditedRead } from '../data/adminDraftRead';   // ruling 66 (Record 582)
import { mayOpen } from '../data/roleScreens';   // Record 592 (F24): the New Case door is the role's, not the page's
import MediaTracker from '../components/MediaTracker';   // Record 613 (Bob 10/8), ruling 157: the recordings tracker
import { recordingUploadTag } from '../data/mediaItems';   // Record 613, ruling 157: a recording is stored only when carrier-produced
import { exampleStamp, isExampleCase, isTesterAccount, actsAsLcOn, keepForViewer, EXAMPLE_MARK, TEST_SCOPE } from '../data/testTerritory';   // Record 613, F22
import DisciplineLadderPanel, { callDisciplineAct, ladderCaller } from '../components/DisciplineLadderPanel';   // Record 611 (F20; Bob 10/8): the ladder's acts
import { may as ladderMay, caseNumberFor } from '../data/disciplineLadder';   // Record 611 (F20; Bob 10/8)
import { SlidesButton } from '../components/SlideDeck';   // ruling 311: hearing preparation as slides (ruling 308)
import { hearingPrepDeck } from '../data/slideDecks';
import { intakeClocks, intakeMissing, receiptProblems } from '../data/disciplineIntake';   // ruling 308 (Bob 2026-10-14): intake by receipt
import { ANALYZE_URL, PLATFORM_NAME } from '../installation';   // ruling 312: the functions host comes from the installation
import { CRAFTS } from '../data/craftTerms';   // ruling 312 phase 1 (c)
import DisciplineMemberActsPanel from '../components/DisciplineMemberActsPanel';   // T5 / D-9
import DisciplineAppealPanel from '../components/DisciplineAppealPanel';   // T5 / D-19
import HearingRecordPanel from '../components/HearingRecordPanel';   // T5 / D-12
import DisciplineBoardPanel from '../components/DisciplineBoardPanel';   // T5 / D-20
import DisciplineWrittenReportPanel from '../components/DisciplineWrittenReportPanel';   // T5 / D-23
import DisciplineProvisionsPanel from '../components/DisciplineProvisionsPanel';   // T5 / D-3, D-13
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

// 6a-08 (2026-09-14, task list section 247): the hearing's dated ACTS, as records of the case. Ruling
// 18's list - demand letters, postponements, cancellation, exhibits, witnesses, pre-hearing meetings,
// the carrier's case - existed here as templates, a log and upload types; nothing said WHEN each act
// happened, which side did it, or what it did to the hearing date. Each act now goes on the case as
// `hearingEvents[]` through src\data\hearingEvents.js (the schedule and the postponement count are
// derived from the acts, never typed), written with logAudit() so the audit trigger records it.
function HearingActs({ disciplineCase, caseId, user, userRole, canAct }) {
  const [form, setForm] = useState({ kind: 'hearing_noticed', on: '', by: 'organization', byName: '', withName: '', hearingDate: '', documentName: '', note: '', mediaClass: 'radio', outcome: 'produced' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const events = Array.isArray(disciplineCase.hearingEvents) ? disciplineCase.hearingEvents : [];
  const fallback = disciplineCase.hearingLog && disciplineCase.hearingLog.hearingDate ? disciplineCase.hearingLog.hearingDate : null;
  const tl = hearingTimeline(events, fallback, disciplineCase.noticeDate || null);   // ruling 158: due from the notice of charge
  const dc = decisionClock(events, disciplineLimits(disciplineCase.applicableAgreement), new Date().toISOString().slice(0, 10));   // T5 / D-14
  const tc = transcriptClock(events, disciplineLimits(disciplineCase.applicableAgreement), new Date().toISOString().slice(0, 10));   // T5 / D-16
  const kind = EVENT_KINDS.find(k => k.kind === form.kind) || EVENT_KINDS[0];
  const docs = (disciplineCase.documents || []).map(d => d.name).filter(Boolean);
  const record = async () => {
    setErr(''); setBusy(true);
    // Section 487: an act that belongs to one side takes that side from the act, not from the picker's default.
    const by = (kind.side === 'carrier' || kind.side === 'organization') ? kind.side : form.by;
    const plan = planHearingEvent({ ...form, by, hearingDateFallback: fallback, actorUid: user?.uid || auth.currentUser?.uid, actorName: user?.name || user?.email, byRole: userRole }, events);
    if (!plan.ok) { setErr(plan.errors.join(' ')); setBusy(false); return; }
    try {
      await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ hearingEvents: arrayUnion(plan.event), updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'Hearing act recorded: ' + plan.event.label + ' ' + plan.event.on));
      await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), { text: 'Hearing act recorded: ' + plan.event.label + ' on ' + plan.event.on + ' (' + plan.event.by + ')' + (plan.event.hearingDate ? ' - hearing date ' + plan.event.hearingDate : '') + (plan.event.withName ? ' - with ' + plan.event.withName : '') + (plan.event.documentName ? ' - document: ' + plan.event.documentName : '') + '.', authorName: user?.name || user?.email, authorRole: userRole, timestamp: serverTimestamp() });
      setForm({ kind: 'hearing_noticed', on: '', by: 'organization', byName: '', withName: '', hearingDate: '', documentName: '', note: '' });
    } catch (e) { setErr('Could not record it: ' + (e && e.code ? e.code : e.message)); }
    setBusy(false);
  };
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title">Hearing acts - the dated record</div>
      <div className="card-subtitle" style={{ marginBottom: 8 }}>Every act on the hearing, with its date, which side did it and the document it rests on. The schedule and the postponement count below are read from these acts, never typed.<RulingRef user={user} n={18} why="Hearing work - demands, postponements, cancellations, exhibits, witnesses and pre-hearing meetings - is part of the Local Chairperson's job, so each act is recorded on the case." /></div>
      <div style={{ fontSize: 12, fontWeight: 700, color: /CANCELLED/.test(tl.summary) ? '#E8A020' : '#DCE7F2', marginBottom: 8 }}>{tl.summary}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: dc.late || (dc.left !== null && dc.left !== undefined && dc.left < 0) ? '#E06C6C' : dc.due ? '#E8A020' : '#8AA4BF', marginBottom: 8 }}>{dc.line}</div>   {/* T5 / D-14 */}
      <div style={{ fontSize: 12, fontWeight: 700, color: tc.late || (tc.left !== null && tc.left !== undefined && tc.left < 0) ? '#E06C6C' : tc.due ? '#E8A020' : '#8AA4BF', marginBottom: 8 }}>{tc.line}</div>   {/* T5 / D-16 */}
      <div style={{ fontSize: 12, fontWeight: 700, color: tl.preservation.unissued ? '#E06C6C' : '#5FB878', marginBottom: 8 }}>{tl.preservation.line}<RulingRef user={user} n={158} why="The demand to preserve radio, phone and video is made before the hearing, as soon as the charge is noticed." /></div>
      {tl.rows.length ? (
        <div className="table-wrapper"><table>
          <thead><tr><th>Date</th><th>Act</th><th>By</th><th>Effect</th><th>Document</th><th>Note</th><th>Recorded by</th></tr></thead>
          <tbody>{tl.rows.map(r => (
            <tr key={r.id}><td>{r.on}</td><td>{r.label}</td><td>{r.by}{r.byName ? ' - ' + r.byName : ''}</td><td>{r.effect || ''}</td><td>{r.documentName || ''}</td><td>{r.note || ''}</td><td style={{ fontSize: 11 }}>{r.recordedByName || r.recordedBy} {String(r.recordedAt || '').slice(0, 10)}</td></tr>
          ))}</tbody>
        </table></div>
      ) : <div style={{ fontSize: 12, color: '#8AA4BF' }}>No act is on record yet. Record the hearing notice first - it sets the schedule.</div>}
      {canAct ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10, padding: 8, border: '1px solid #2E4A6B' }}>
          <select aria-label="Which act to record" className="form-select" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })}>{EVENT_KINDS.map(k => <option key={k.kind} value={k.kind}>{k.label}</option>)}</select>
          <input aria-label="Date" className="form-input" type="date" value={form.on} onChange={e => setForm({ ...form, on: e.target.value })} title="The date of the act" />
          {kind.side === 'either' ? <select aria-label="Party" className="form-select" value={form.by} onChange={e => setForm({ ...form, by: e.target.value })}><option value="organization">by the organization</option><option value="carrier">by the carrier</option></select> : <div style={{ fontSize: 12, color: '#8AA4BF', alignSelf: 'center' }}>{kind.side === 'both' ? 'both sides' : 'the ' + kind.side + '\'s act'}</div>}
          <input aria-label="Who (name / title; optional)" className="form-input" placeholder="Who (name / title; optional)" value={form.byName} onChange={e => setForm({ ...form, byName: e.target.value })} />
          {kind.dated === 'hearingDate' ? <input aria-label="Hearing date" className="form-input" type="date" value={form.hearingDate} onChange={e => setForm({ ...form, hearingDate: e.target.value })} title="The hearing date this act sets" />
            : kind.media ? <span style={{ display: 'flex', gap: 6 }}><select aria-label="Media class" className="form-select" value={form.mediaClass} onChange={e => setForm({ ...form, mediaClass: e.target.value })} title="Which media the demand covers">{PRESERVATION_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}</select>{kind.kind === 'preservation_answered' ? <select className="form-select" value={form.outcome} onChange={e => setForm({ ...form, outcome: e.target.value })} title="The carrier's answer">{PRESERVATION_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}</select> : null}</span>
            : <div />}
          <select aria-label="Document name" className="form-select" value={form.documentName} onChange={e => setForm({ ...form, documentName: e.target.value })}><option value="">{kind.doc ? 'Document on the case: ' + kind.doc + '…' : 'Document (optional)…'}</option>{docs.map(n => <option key={n} value={n}>{n}</option>)}</select>
          {kind.oral ? <input aria-label="With whom for the carrier (name and title; required)" className="form-input" style={{ gridColumn: '1 / span 2' }} placeholder="With whom for the carrier (name and title; required)" value={form.withName} onChange={e => setForm({ ...form, withName: e.target.value })} /> : null}   {/* T5 / D-8 */}
          {kind.oral ? <div style={{ gridColumn: '1 / span 2', fontSize: 12, color: '#E8A020' }}>&#9888; {ORAL_ACT_WARNING}</div> : null}
          <input aria-label="What was said or written" className="form-input" style={{ gridColumn: '1 / span 2' }} placeholder={kind.oral ? 'What was said (required)' : 'Note (optional)'} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          {err ? <div style={{ gridColumn: '1 / span 2', color: '#E06C6C', fontSize: 12 }}>&#9888; {err}</div> : null}
          <button className="btn btn-primary" disabled={busy || !form.on} onClick={record}>Record this act</button>
        </div>
      ) : null}
    </div>
  );
}

// Helper: wrap mutation data with audit metadata for onWrite trigger capture
// G3.3: logAudit() at 27 mutation sites
// Record 586 (functions audit F3): the history's actor is `updatedBy` - this signed-in account's uid, which
// firestore.rules requires to equal request.auth.uid. `_auditActor` is kept only as the display text it always was.
const logAudit = (data, actor, reason) => ({
  ...data,
  updatedBy: (auth.currentUser && auth.currentUser.uid) || '',
  _auditActor: actor || 'unknown',
  _auditReason: reason || ''
});

// Renders the extracted charging-letter text as a formal, document-style record.
// Forgiving parser: recognizes the known section headings, sets verbatim charge/
// specification text in indented serif block-quotes, and formats "Label: value" lines.
const RECORD_HEADINGS = ['DATE OF LETTER', 'ADDRESSEE', 'ADDRESSOR', 'HEARING / INVESTIGATION SCHEDULE', 'THE CHARGE', 'SPECIFICATION', 'CHARGING OFFICER', 'HEARING OFFICER', 'WITNESSES', 'ADDITIONAL PERSONS'];
function renderExtractedRecord(text) {
  const clean = (s) => s.replace(/\*\*/g, '').replace(/^\*+|\*+$/g, '').trim();
  const lines = (text || '').split('\n');
  const out = [];
  let inQuote = false;
  lines.forEach((raw, i) => {
    const line = clean(raw);
    if (!line || /^[-—_]{2,}$/.test(line)) return;
    const upper = line.toUpperCase();
    const isHeading = RECORD_HEADINGS.some(h => upper.startsWith(h));
    const isSub = /^CHARGE (ONE|TWO|THREE|FOUR|FIVE|SIX|SEVEN|EIGHT|\d+)\b/i.test(line);
    if (isHeading) {
      inQuote = upper.startsWith('THE CHARGE') || upper.startsWith('SPECIFICATION');
      out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#C9A84C', borderBottom: '1px solid rgba(201,168,76,0.35)', paddingBottom: 5, marginTop: out.length ? 20 : 0, marginBottom: 9 }}>{line.replace(/:\s*$/, '')}</div>);
      return;
    }
    if (isSub) {
      out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 700, color: '#E8A020', fontSize: 13, marginTop: 12, marginBottom: 5 }}>{line.replace(/:\s*$/, '')}</div>);
      return;
    }
    if (inQuote) {
      out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, lineHeight: 1.65, color: '#ECEFF2', fontStyle: 'normal', borderLeft: '3px solid #C9A84C', paddingLeft: 15, marginBottom: 9 }}>{line}</div>);
      return;
    }
    const m = line.match(/^([^:]{2,42}):\s*(.*)$/);
    if (m && m[2] !== '') {
      const notStated = /not stated/i.test(m[2]);
      out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, lineHeight: 1.7, marginBottom: 3 }}><span style={{ color: '#8AA4BF' }}>{m[1]}: </span><span style={{ color: notStated ? '#6F7B87' : '#F4F6F8', fontStyle: notStated ? 'normal' : 'normal' }}>{m[2]}</span></div>);
    } else {
      out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, lineHeight: 1.7, color: '#F4F6F8', marginBottom: 3 }}>{line}</div>);
    }
  });
  return out;
}

// Lightweight formatter for AI-generated formal documents (markdown subset:
// # / ## / ### headings, **bold**, --- rules). Judicial serif styling.
function renderInline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i} style={{ color: '#F4F6F8' }}>{p.replace(/\*\*/g, '')}</strong>;
    if (/^\*[^*\n]+\*$/.test(p)) return <span key={i}>{p.replace(/^\*|\*$/g, '')}</span>;
    return <span key={i}>{p}</span>;
  });
}
function renderFormalDoc(text) {
  const lines = (text || '').split('\n');
  const out = [];
  lines.forEach((raw, i) => {
    const t = raw.trim();
    if (!t) { out.push(<div key={i} style={{ height: 8 }} />); return; }
    if (/^[-—_*]{3,}$/.test(t)) { out.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid rgba(201,168,76,0.3)', margin: '16px 0' }} />); return; }
    const h1 = t.match(/^#\s+(.*)$/), h2 = t.match(/^##\s+(.*)$/), h3 = t.match(/^###\s+(.*)$/);
    if (h1) { out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 16, fontWeight: 700, letterSpacing: 1, color: '#F4F6F8', textAlign: 'center', marginBottom: 8 }}>{renderInline(h1[1])}</div>); return; }
    if (h2) { out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#C9A84C', borderBottom: '1px solid rgba(201,168,76,0.35)', paddingBottom: 5, marginTop: 20, marginBottom: 9 }}>{renderInline(h2[1])}</div>); return; }
    if (h3) { out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 13, fontWeight: 700, color: '#E8A020', marginTop: 12, marginBottom: 4 }}>{renderInline(h3[1])}</div>); return; }
    const isVerify = /\[VERIFY/i.test(t);
    out.push(<div key={i} style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, lineHeight: 1.7, color: isVerify ? '#E0B44C' : '#DDE3E8', marginBottom: 4 }}>{renderInline(t)}</div>);
  });
  return out;
}

// Investigation deadlines — days from Carrier's first knowledge of incident
// CRITICAL: These are deadlines ON THE CARRIER. If missed, discipline cannot be assessed.
//
// 2026-09-26, task list section 442: the hand-typed INVESTIGATION_DEADLINES table that lived here is
// GONE. The numbers now come from src/data/disciplineAnswerLimits.js - each one a projection of a
// verbatim quote at a cited line in data/agreements/* - through screenDeadlines(), which resolves the
// case's applicableAgreement with canonicalAgreementKey (the claim path's aliases). The old table was
// keyed by names no case carries, so a Conrail case ('... (eff. 1978)') and every CSX-territory case
// silently got no deadline even after section 440 put Conrail at 20/20. Values are unchanged:
// Amtrak 30/30/30 + 7-day meeting; CSX 15/15/30; D&H/CP 10/10/21; Conrail 20/20/20. Conrail Mechanical
// Supervisors and BN stay off the list (not held) and show NO_DEADLINE_ON_FILE (section 364).
//
// The explanatory note shown with a deadline, by canonical agreement key. Text only - no number here
// may differ from the module; the numbers are read from it.
const DEADLINE_NOTES = {
  'Amtrak Yardmaster Agreement (eff. Jan 1, 1983; Labor Relations Printout, Nov 1, 2000)':
    'Amtrak Rule 36 (amended 2000) — letter of intent barred once the supervisor has known of the offense more than 30 calendar days; intent meeting within 7 days from the employee\'s RECEIPT of the letter of intent (if management fails to attend, the letter is withdrawn and canceled); hearing to begin within 30 calendar days of the supervisor\'s first knowledge; written decision within 30 calendar days after the hearing closes.',
  'CSX Yardmaster System Agreement (eff. July 1, 2002)':
    'CSX Art. 21 — 15 days from supervisor first knowledge to hearing. Decision within 30 days.',
  'Delaware & Hudson / Canadian Pacific Yardmaster Agreement (codified 2007)':
    'D&H/CP Art. 13 — 10-day notice, 10-day scheduling, 21-day decision. If not scheduled in time: NO discipline may be assessed.',
  'Conrail Yardmasters Agreement (eff. July 1, 1978)':
    'Conrail/RYA 1978 Rule 6-A-1(b) - hearing to begin within 20 calendar days of the immediate supervisor\'s first knowledge; written decision within 20 calendar days after the hearing closes. The time limits may be extended by mutual agreement.',
};

// THE NOTICE WINDOW, OR NOTHING (2026-09-22, section 364, D3).
//
// This returned `|| 10` - ten days invented for any agreement not in the table, then written onto the
// case as a hard date and described in a note as being "per applicable agreement". There is no safe
// default for a time limit: too short and an officer presses a dead objection, too long and he waives
// a live one, and either way the platform has cited an agreement it never read.
//
// Returns null when the agreement is not catalogued. Every caller must handle null by saying so.
function getNoticeDays(agreement) {
  const d = screenDeadlines(agreement);
  return d && typeof d.noticeDays === 'number' ? d.noticeDays : null;
}

// What the screen says instead of a date it does not have.
const NO_DEADLINE_ON_FILE =
  'No notice window is recorded on this platform for this agreement. That does not mean there is '
  + 'none - it means this platform does not hold it. Check the controlling agreement, and ask your '
  + 'General Chairperson.';

const DISCIPLINE_TYPES = [
  'Not Stipulated',
  'As Stated in Charging Letter',
];

const DECISION_TYPES = [
  'Written Reprimand',
  'Demerit',
  'Suspension',
  'Dismissal',
  'Time / Discipline Held in Abeyance',
  'Held in Abeyance',
  'Charges Dismissed',
  'Discipline Reduced',
  'Exonerated',
  'Dropped Charges',
  'Pending',
];

const CASE_STAGES = [
  { key: 'notice_received', label: 'Notice Received', icon: '📋' },
  { key: 'investigation_ordered', label: 'Investigation Ordered', icon: '⚖️' },
  { key: 'investigation_complete', label: 'Investigation Complete', icon: '📝' },
  { key: 'appeal_filed', label: 'Appeal Filed', icon: '📤' },
  { key: 'decision_pending', label: 'Decision Pending', icon: '⏳' },
  { key: 'resolved', label: 'Resolved', icon: '✅' },
];

const STRENGTH_RATINGS = ['Strong', 'Moderate', 'Weak', 'Unknown'];
const LC_RECOMMENDATIONS = [
  'Full Exoneration',
  'Reduction of Discipline',
  'Accept Discipline',
  'Appeal to GC',
  'Arbitration',
];

const HEARING_DOCUMENT_TYPES = [
  'Investigation Transcript',
  'Postponement Notice',
  'Cancellation Notice',
  'Hearing Correspondence',
  'Carrier Pre-Hearing Notice',
  'Union Pre-Hearing Submission',
  'Witness List',
  'Other Hearing Document',
];

const APPEAL_DOCUMENT_TYPES = [
  'Timeslip',
  'Pay Record',
  'Crew Board Screenshot',
  'Witness Statement',
  'Photo / Image',
  'Rule Reference',
  'Appeal Letter',
  'Board Award / PLB Award',
  'Arbitration Award',
  'Precedent Case',
  'Carrier Correspondence — Post Hearing',
  'Union Submission — Appeal',
  'Other Appeal Exhibit',
];

// Record 611 (F20; Bob 10/8, decision (d)): A CASE NUMBER IS THE SERVER'S. It used to be drawn here, by the browser,
// from counters/discipline_<committee>_<year> in a transaction of its own (F-4, SS193) - so any officer's browser could
// advance the counter, and a number could be drawn for a case that was never opened. The disciplineAct Cloud Function
// now draws it in the SAME transaction that opens the case (functions\index.js), in the same format (DISC-GO342-2026-007),
// from the same counter; firestore.rules closes the counters to every browser. The A5 refusal (no committee, no number:
// a number minted under a committee nobody established is the wrong-value-is-worse-than-absent case, doctrine 5) lives
// with the format in src\data\disciplineLadder.js caseNumberFor(), which this page also calls before it writes anything.

// F-10 (SS193): App.js puts the TOKEN role on the user - 'admin' | 'general_chair' | 'local_chair' |
// 'tester' (App.js:173-178). This file was written against 'lc' / 'gc' and no officer ever matched:
// a General Chairperson opened a case as a member. The stored vocabulary (visibleTo, uploadedByRole)
// stays 'member' | 'lc' | 'gc' | 'admin'; this is the one place the two are reconciled.
function uiRole(user, kase) {
  // Record 613 (Bob 10/8), F22: a tester works an EXAMPLE in the test territory as its Local Chairperson would - the
  // rules' testerSandbox() arm and the ladder's testerOnExample(). Anywhere else he is what he always was here: 'member'.
  if (actsAsLcOn(user, kase)) return 'lc';
  const r = user && user.role;
  if (r === 'admin' || (user && user.isAdmin)) return 'admin';
  if (r === 'general_chair' || r === 'gc') return 'gc';
  if (r === 'local_chair' || r === 'lc') return 'lc';
  return 'member';
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  return diff;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric'
  });
}

// ─── NEW DISCIPLINE CASE FORM ────────────────────────────────────────────────
// F-3(b), 2026-09-09 (task list SS172): every generated analysis is read under this label - including
// the ones already stored on cases, which carry invented award numbers and are not edited (they are
// the record). New outputs are labelled and citation-stripped by the function itself.
// F-17 (SS202, 2026-09-10): a label that says citations were withheld when they were not is worse than
// no label. The function writes its own label as the first line of every output it has produced since
// F-3(b); text that carries it needs nothing from here. Text that lacks it was generated BEFORE the
// rule, still holds every award number the model invented, and is told so - it is the record and is
// not edited, but nobody reads it under a banner that promises the opposite.
const FUNCTION_LABEL = 'DRAFT REASONING - NOT AUTHORITY.';
function DraftReasoningBanner({ text }) {
  if (String(text || '').trimStart().startsWith(FUNCTION_LABEL)) return null;
  return (
    <div style={{ background: '#2D0A0A', border: '1px solid #E84040', borderRadius: 6, padding: '6px 10px', marginBottom: 6, fontSize: 11.5, color: '#FFB0B0', fontWeight: 700 }}>
      GENERATED BEFORE THE CITATION RULE (2026-09-10) — NOT AUTHORITY. Every award, Board or case number in this text is UNVERIFIED and has NOT been withheld; the model wrote them. Regenerate to apply the current rule. Nothing here may be cited to a carrier or a Board until an officer has read the award and the agreement.
    </div>
  );
}
// F-20 (SS203): the charges as the reader needs them - one block per labelled section of the letter,
// fields as bullets, the verbatim charge and specification as their own paragraphs, 14px. The
// extraction has always returned this shape line by line; the Overview row collapsed the lines.
// a heading is all-caps words (a parenthetical may be mixed case) up to a colon; the rest of the line is its value
const HEADING_RE = /^((?:[A-Z0-9/&—–\-.,'()]+|\([^)]*\))(?:\s+(?:[A-Z0-9/&—–\-.,'()]+|\([^)]*\)))*):\s*(.*)$/;   // DATE OF LETTER:  /  ADDRESSEE (Charged Employee):
const SUBHEAD_RE = /^(Charge\s+\w+|CHARGE\s+\w+)\s*:\s*(.*)$/;     // Charge One:
const FIELD_RE = /^([A-Z][A-Za-z /]{1,40}):\s+(.+)$/;               // Name: Casey Harlan (the fictional fixture, ruling 165)
function ChargesView({ text }) {
  const src = plainMd(text);
  if (!src || !String(src).trim()) return <span>—</span>;
  const lines = String(src).split(/\r?\n/).map(l => l.trim()).filter(l => l && !/^-{3,}$/.test(l));
  if (!lines.some(l => HEADING_RE.test(l))) {
    return <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.7 }}>{src}</div>;
  }
  const out = [];
  lines.forEach((l, i) => {
    let m;
    if ((m = HEADING_RE.exec(l)) && /[A-Z]{3,}/.test(m[1])) {
      out.push(<div key={i} style={{ fontSize: 12, fontWeight: 700, color: '#E8A020', letterSpacing: 1, marginTop: out.length ? 14 : 0, marginBottom: 4 }}>{m[1]}:</div>);
      if (m[2] && m[2].trim()) out.push(<div key={i + 'v'} style={{ fontSize: 14, lineHeight: 1.7, color: '#F4F6F8', marginLeft: 12 }}>{m[2].trim()}</div>);
    } else if ((m = SUBHEAD_RE.exec(l))) {
      out.push(<div key={i} style={{ fontSize: 14, fontWeight: 700, color: '#F4F6F8', marginTop: 8, marginLeft: 12 }}>{m[1]}</div>);
      if (m[2] && m[2].trim()) out.push(<div key={i + 'v'} style={{ fontSize: 14, lineHeight: 1.7, color: '#C8D8E8', marginLeft: 24 }}>{m[2].trim()}</div>);
    } else if ((m = FIELD_RE.exec(l))) {
      out.push(<div key={i} style={{ display: 'flex', gap: 8, fontSize: 14, lineHeight: 1.7, marginLeft: 12 }}><span style={{ color: '#8AA4BF' }}>•</span><span><span style={{ color: '#8AA4BF' }}>{m[1]}:</span> <span style={{ color: '#F4F6F8' }}>{m[2]}</span></span></div>);
    } else {
      out.push(<div key={i} style={{ fontSize: 14, lineHeight: 1.7, color: '#C8D8E8', marginLeft: 12, marginBottom: 4 }}>{l}</div>);
    }
  });
  return <div>{out}</div>;
}
// F-16 (SS202): generated text and extracted charges arrive as markdown; the panels are pre-wrap text.
// Strip the markers for display - emphasis is lost, nothing else is. The stored text is untouched.
function plainMd(text) {
  if (text === null || text === undefined) return text;
  return String(text)
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')          // headings
    .replace(/(\s{2,})#{1,6}\s+/g, '$1')             // headings inside text whose newlines were flattened
    .replace(/^\s{0,3}>\s?/gm, '')              // block quotes
    .replace(/^\s{0,3}(?:-{3,}|\*{3,})\s*$/gm, '')  // rules
    .replace(/\*\*(.+?)\*\*/g, '$1')            // bold
    .replace(/(^|[^*\w])\*(?!\s)([^*\n]+?)\*(?!\w)/g, '$1$2');   // italics (a bare * bullet is left alone)
}

function CollapseSectionLocal({ title, icon, color, defaultOpen = true, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ marginBottom: 16, border: `1px solid ${color || '#2E4A6B'}`, borderRadius: 8, overflow: 'hidden' }}>
      <div {...activate(() => setOpen(!open))} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#0D1B2A', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon && <span>{icon}</span>}
          <span style={{ fontSize: 12, fontWeight: 700, color: color || '#E8A020' }}>{title}</span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--steel)' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div style={{ padding: '14px 16px' }}>{children}</div>}
    </div>
  );
}

function NewDisciplineCase({ user, onSaved, onCancel }) {
  const [form, setForm] = useState({
    memberName: user?.name || '',
    memberEmail: user?.email || '',
    craft: user?.craft || '',
    railroad: user?.railroad || '',
    // ruling 100 (2026-09-10): the charged employee's work location resolves the LCA designation
    // (scopeKey) that names the Local Chairperson with authority over the case - the same key, the
    // same resolver, the same rule arm as a claim.
    workLocation: user?.workLocation || '',
    scopeKey: resolveScopeKey(user?.craft || '', user?.railroad || '', user?.workLocation || ''),
    // F-14 (SS193/SS197): user.craft and user.railroad are never set by App.js; the officer's own
    // committee claim (App.js:180) is what the token knows. The lookup stays for the day a member
    // profile carries craft and railroad.
    // A5: no 'GO-342' last resort. Empty means UNKNOWN and the save refuses; a plausible wrong
    // committee is the one outcome nobody catches.
    generalCommittee: COMMITTEE_LOOKUP[user?.craft || '']?.[user?.railroad || ''] || (user && user.committee) || '',
    applicableAgreement: '',
    disciplineType: '',
    suspensionDays: '',
    noticeDate: '',
    // Ruling 308 (Bob 2026-10-12; built 2026-10-14): the letter of intent is its own letter with its own dates, and RECEIPT
    // is recorded, never assumed from a letter's date. firstKnowledgeOn: the supervisor's first knowledge, where stated.
    intentLetterDate: '',
    intentReceivedOn: '',
    chargeReceivedOn: '',
    firstKnowledgeOn: '',
    noticeMonth: '',
    noticeDay: '',
    noticeYear: '',
    noticeDescription: '',
    chargesDescription: '',
    heldInAbeyance: false,
    // Record 611 (F20; Bob 10/8): decision and decisionDate are no longer on the new-case form. They had no input here,
    // and a decision is now recorded only by the disciplineAct function ("Record decision" on the case).
    boardAwards: [],
    precedentResearch: '',
    exhibitList: '',
    casePresentation: '',
  });
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [analysis, setAnalysis] = useState({ objective: '', union: '', carrier: '' });
  const [analysisLoading, setAnalysisLoading] = useState(null);
  const [noticeFile, setNoticeFile] = useState(null);
  const [intentFile, setIntentFile] = useState(null);   // ruling 308: the letter of intent, its own slot
  const [additionalExhibitFile, setAdditionalExhibitFile] = useState(null);
  const [noticeFileB64, setNoticeFileB64] = useState(null);
  const [noticeFileType, setNoticeFileType] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editingExtract, setEditingExtract] = useState(false);
  const [editingObjections, setEditingObjections] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [editingExhibits, setEditingExhibits] = useState(false);
  const [editingCase, setEditingCase] = useState(false);
  const [extractingExhibits, setExtractingExhibits] = useState(false);
  const [extractingCase, setExtractingCase] = useState(false);
  const [extractingPrecedent, setExtractingPrecedent] = useState(false);
  const [editingPrecedent, setEditingPrecedent] = useState(false);
  const [newAward, setNewAward] = useState({ boardType: 'PLB', awardNumber: '', date: '', summary: '', link: '' });
  const [extractingObjections, setExtractingObjections] = useState(false);
  const [extractingQuestions, setExtractingQuestions] = useState(false);
  // Greg's note #3 (10/4): a regenerate can be cancelled, and the previous set restored.
  const prevGen = useRef({});
  const abortGen = useRef({});
  const [restorable, setRestorable] = useState({});
  const isRealGen = (v) => !!v && !/^(\u2699|Error|Could not)/.test(v);
  const beginGen = (key) => {
    if (isRealGen(form[key])) prevGen.current[key] = form[key]; else delete prevGen.current[key];
    setRestorable(r => ({ ...r, [key]: false }));
    const c = new AbortController(); abortGen.current[key] = c; return c.signal;
  };
  const cancelGen = (key) => { const c = abortGen.current[key]; if (c) c.abort(); setForm(prev => ({ ...prev, [key]: prevGen.current[key] || '' })); };
  const restoreGen = (key) => { if (prevGen.current[key] !== undefined) setForm(prev => ({ ...prev, [key]: prevGen.current[key] })); setRestorable(r => ({ ...r, [key]: false })); };
  const [extractingLetter, setExtractingLetter] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const FN_URL = ANALYZE_URL;

  // Reusable generators — accept the extracted text directly so they can be
  // auto-fired right after extraction (before state updates land) or called
  // manually from the Regenerate button using current form state.
  // RULING 201 (built 2026-09-22): procedural objections are the officer's own, typed from the case
  // record and the catalogued provisions. Nothing here asks a model to write one.

  const runQuestions = async (descText) => {
    const desc = descText || form.chargesDescription;
    if (!desc || desc.startsWith('⚙')) return;
    setExtractingQuestions(true);
    const signal = beginGen('investigationQuestions');
    set('investigationQuestions', '⚙ Generating investigation questions...');
    try {
      const response = await fetch(FN_URL, {
        signal,
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({
          mode: 'discipline_questions',
          agreement: form.applicableAgreement || 'railroad labor agreement',
          description: desc
        })
      });
      const data = await response.json();
      const out = data.result || 'Could not generate questions. Tap Regenerate to try again.';
      set('investigationQuestions', out);
      if (prevGen.current.investigationQuestions) setRestorable(r => ({ ...r, investigationQuestions: true }));
      return out;
    } catch (e) {
      if (e && e.name === 'AbortError') return null;   // cancelled: cancelGen already put the previous set back
      set('investigationQuestions', 'Error generating questions. Tap Regenerate to try again.');
      return null;
    } finally {
      setExtractingQuestions(false);
    }
  };

  // Combine the four content boxes + cited awards into one materials block.
  // Accepts overrides so a chained auto-run can pass just-generated text
  // (which isn't in form state yet).
  const caseMaterials = (o = {}) => {
    const g = (k) => (o[k] !== undefined ? o[k] : form[k]);
    const awards = (g('boardAwards') || []).map(a =>
      `${a.boardType} Award #${a.awardNumber}${a.date ? ' (' + a.date + ')' : ''}: ${a.summary}${a.link ? ' [' + a.link + ']' : ''}`
    ).join('\n');
    return [
      'CHARGING LETTER EXTRACTION:\n' + (g('chargesDescription') || '(none provided)'),
      "MEMBER'S STATEMENT:\n" + (g('noticeDescription') || '(none provided)'),
      'PROCEDURAL OBJECTIONS:\n' + (g('proceduralObjections') || '(none provided)'),
      'INVESTIGATION QUESTIONS:\n' + (g('investigationQuestions') || '(none provided)'),
      'CITED BOARD AWARDS:\n' + (awards || '(none cited)')
    ].join('\n\n---\n\n');
  };

  // RULING 201 (built 2026-09-22): web research never supplies an award or a citation. Awards reach a
  // case only as the officer enters them, and only through data\authorities under ruling 198.

  const runExhibits = async (overrides) => {
    setExtractingExhibits(true);
    const signal = beginGen('exhibitList');
    set('exhibitList', '⚙ Generating exhibit list...');
    try {
      const response = await fetch(FN_URL, {
        signal,
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({
          mode: 'discipline_exhibits',
          agreement: form.applicableAgreement || 'railroad labor agreement',
          description: caseMaterials(overrides)
        })
      });
      const data = await response.json();
      const out = data.result || 'Could not generate exhibit list. Tap Regenerate to try again.';
      set('exhibitList', out);
      if (prevGen.current.exhibitList) setRestorable(r => ({ ...r, exhibitList: true }));
      return out;
    } catch (e) {
      if (e && e.name === 'AbortError') return null;
      set('exhibitList', 'Error generating exhibit list. Tap Regenerate to try again.');
      return null;
    } finally {
      setExtractingExhibits(false);
    }
  };

  const runCase = async (overrides) => {
    setExtractingCase(true);
    set('casePresentation', '⚙ Building the defense presentation...');
    try {
      const response = await fetch(FN_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({
          mode: 'discipline_case',
          agreement: form.applicableAgreement || 'railroad labor agreement',
          description: caseMaterials(overrides)
        })
      });
      const data = await response.json();
      set('casePresentation', data.result || 'Could not build the case presentation. Tap Regenerate to try again.');
    } catch (e) {
      set('casePresentation', 'Error building the case presentation. Tap Regenerate to try again.');
    } finally {
      setExtractingCase(false);
    }
  };

  const handleSubmit = async () => {
    // The old refusal named nothing - and until section 353 one of the three fields had no input at all,
    // so he could not have supplied it however hard he looked. Name them.
    // Ruling 308: a case may open on a letter of intent before any charge is preferred - one letter's date is required.
    const missing = intakeMissing(form);
    if (missing.length) {
      alert('Still needed: ' + missing.join(', ') + '.');
      return;
    }
    const dateProblems = receiptProblems(form);
    if (dateProblems.length) { alert('Check the dates: ' + dateProblems.join(' ')); return; }
    // Record 611 (F20; Bob 10/8): the committee is checked BEFORE anything is written - A5, no committee, no number. The
    // number itself is drawn by the disciplineAct function when it opens the case.
    // Record 613 (Bob 10/8), F22: a tester's case is an EXAMPLE in the test territory, or it is not written at all. It is
    // drafted like every case and opened by disciplineAct, which numbers an example EXAMPLE-<committee>-<id> - never from
    // the committee's counter (src\data\disciplineLadder.js exampleCaseNumberFor()).
    const ex = exampleStamp(user);
    if (!ex.ok) { alert(ex.why); return; }
    try { caseNumberFor(ex.stamp.generalCommittee || form.generalCommittee, new Date().getFullYear(), 1); } catch (e) { alert(e.message); return; }
    setSaving(true);
    try {

      // The investigation deadline, ONLY where the agreement actually gives one (section 364, D3).
      const deadlineDays = getNoticeDays(form.applicableAgreement);
      const clocks = screenDeadlines(form.applicableAgreement);   // section 442: the sourced record
      const agreementDeadlineNote = (clocks && DEADLINE_NOTES[clocks.agreementKey]) || '';
      // Ruling 308 + 314 (disciplineIntake.js): the intent meeting from RECEIPT, the hearing from the supervisor's first
      // knowledge where recorded (else from the notice of charge, said to be a latest bound), the first day as day one.
      const ic = intakeClocks(form, clocks);

      const caseData = {
        ...form,
        ...ex.stamp,   // Record 613 (Bob 10/8), F22: isExample, the test territory, the tester's own e-mail - or nothing
        // 2026-09-09 (SS172): the create rule has required createdBy == the signed-in uid since
        // 2026-07-30 and this page never wrote it - no case could be created by anyone.
        createdBy: (user && user.uid) || (auth.currentUser && auth.currentUser.uid) || '',
        status: 'draft',   // Record 611 (F20; Bob 10/8): written as a draft; the disciplineAct function opens and numbers it
        // Absent rather than invented. A reader who finds no deadline here learns the true thing -
        // that this platform holds none - instead of a date that looks sourced and is not.
        investigationDeadline: ic.investigationDeadline,
        investigationDeadlineDays: deadlineDays,
        investigationDeadlineBasis: ic.investigationDeadlineBasis || null,
        investigationDeadlineNote: deadlineDays === null ? NO_DEADLINE_ON_FILE : [ic.investigationDeadlineNote, agreementDeadlineNote].filter(Boolean).join(' '),
        intentMeetingDue: ic.intentMeetingDue,
        intentMeetingNote: ic.intentMeetingNote || null,
        intentBarFlag: ic.intentBarFlag || null,
        hearingDays: clocks?.hearingDays ?? null,
        decisionDays: clocks?.decisionDays ?? null,
        postponements: 0,
        witnesses: [],
        hearingLog: null,
        lcAssessment: null,
        // Record 611: no decision is written here - only the disciplineAct function records one.
        openedBy: user?.email || '',
        openedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        documents: [],
      };

      // F-6 (SS193): this was `const ref = await addDoc(...)`, which shadowed the storage `ref` import
      // for the rest of this block - the upload four lines down called a DocumentReference and threw.
      // F-22 (SS205): and the case was written BEFORE the upload, so when the upload threw (F-6) or was
      // refused (F-21) the case survived without its letter - DOC-2 found exactly that on the 7/13 case.
      // Now the id is minted first, the letter goes to Storage first, and the case is written once with
      // its documents row in it. A failed upload leaves no case, and the alert below says which it was.
      const caseRef = draftId ? doc(db, 'disciplineCases', draftId) : doc(collection(db, 'disciplineCases'));   // Record 611: the saved draft, when there is one
      // F15 second half (Bob, 2026-10-09: "Recommendation agreed" - decisions list item 7 (a)): the case DRAFT is written
      // FIRST and the charging letter stored after it, so every discipline file lands under a case record that already
      // exists. F-22's worry (a case surviving without its letter) no longer applies: what survives a failed upload is a
      // DRAFT - its author's alone (ruling 56), never opened or numbered - and the message says the letter was not stored.
      if (draftId) {
        const upd = { ...caseData }; delete upd.documents;   // a letter stored by an earlier try stays
        await updateDoc(caseRef, logAudit(upd, user?.email || auth.currentUser?.email, 'Draft completed for opening'));
      } else {
        await setDoc(caseRef, logAudit({ ...caseData, documents: [] }, user?.email || auth.currentUser?.email, 'Discipline case drafted for opening'));
        setDraftId(caseRef.id);
      }
      let documents = [];
      if (noticeFile) {
        const storageRef = ref(storage, `discipline/${caseRef.id}/${noticeFile.name}`);
        // P-5: the object carries who and which committee, all-or-none, like claims/ (ruling 74).
        // RULINGS 187/190: the file carries the case's committee, territory and member, so storage.rules can
        // admit exactly those the case rule admits. One helper, tested (scripts\\test-discipline-storage.mjs).
        const objTag = disciplineObjectTag(caseData);
        // Record 591 (ruling 247): the link is NOT fetched here. The file is unreadable to everyone but the
        // administrator until the scanner marks it clean, so asking for it now was refused and aborted the case
        // after the bytes had landed. The path is stored; View fetches the link when clicked (src/data/scannedFile.js).
        const url = '';
        try {
          await uploadBytes(storageRef, noticeFile, objTag);
        } catch (up) {
          const why = (up && up.code) ? up.code : ((up && up.message) || 'unknown');
          throw Object.assign(new Error('The charging letter could not be stored (' + why + '). The case was saved as a DRAFT only - not opened, not numbered. Press "Open Discipline Case" again to retry the letter, or open the draft from the case list.'), { ycpStage: 'upload' });
        }
        documents = [{
          name: noticeFile.name,
          url,
          storagePath: storageRef.fullPath,   // Record 591 (ruling 247)
          type: 'Discipline Notice',
          uploadedBy: user?.email || '',
          uploadedAt: new Date().toISOString(),
          visibleTo: ['member', 'lc', 'gc', 'admin'],
          isOriginalNotice: true,
          isOriginalChargeDocument: true,  // F-9: the Documents tab pins by this key
        }];
      }
      // Record 611 (F20; Bob 10/8, decision (d)): the case is written as a DRAFT - the same document a Save Draft already
      // made, when there is one - and then OPENED by the disciplineAct function, which draws its number from the
      // committee's counter and moves it to Notice Received in one transaction. If the open is refused, the draft stands
      // (its author's alone, ruling 56), the message says so, and pressing Open again opens that same draft.
      // Ruling 308: the letter of intent, stored beside (never as) the charging letter.
      if (intentFile) {
        const intentRef = ref(storage, `discipline/${caseRef.id}/intent_letter_${Date.now()}_${intentFile.name}`);
        try {
          await uploadBytes(intentRef, intentFile, disciplineObjectTag(caseData));
        } catch (up) {
          const why = (up && up.code) ? up.code : ((up && up.message) || 'unknown');
          throw Object.assign(new Error('The letter of intent could not be stored (' + why + '). The case was saved as a DRAFT only - not opened, not numbered. Press "Open Discipline Case" again to retry, or open the draft from the case list.'), { ycpStage: 'upload' });
        }
        documents.push({
          name: intentFile.name, url: '', storagePath: intentRef.fullPath,
          type: 'Notice of Intent', uploadedBy: user?.email || '', uploadedAt: new Date().toISOString(),
          visibleTo: ['member', 'lc', 'gc', 'admin'], isIntentLetter: true,
        });
      }
      if (documents.length) {
        // The letter is stored; its documents row is attached to the draft written above.
        await updateDoc(caseRef, logAudit({ documents, updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'Original charging letter attached'));
      }
      if (noticeFile) setNoticeFile(null);   // stored with the draft; a second press does not upload it again
      if (intentFile) setIntentFile(null);
      let opened;
      try {
        opened = await callDisciplineAct({ caseId: caseRef.id, kind: 'advance' });
      } catch (oe) {
        throw Object.assign(new Error('The case was saved as a DRAFT but NOT opened: ' + ((oe && oe.message) || 'the act was refused')
          + '. Nothing else was written. Press "Open Discipline Case" again, or open the draft from the case list.'), { ycpStage: 'open' });
      }
      const caseNumber = (opened && opened.caseNumber) || '(none)';

      // Add system note
      await addDoc(collection(db, 'disciplineCases', caseRef.id, 'notes'), {
        // Section 364 (D3): the deadline sentence is written only when there IS one. It used to read
        // '... per applicable agreement' over a number this platform invented, and it would now throw on
        // the null - so the null is handled by saying the true thing instead.
        text: `Discipline case opened by ${user?.name || user?.email}. Case number assigned: ${caseNumber}.`
          + (ic.investigationDeadline
              ? ` Investigation deadline: ${new Date(ic.investigationDeadline + 'T12:00:00').toLocaleDateString('en-US', {month:'long',day:'numeric',year:'numeric'})} (${deadlineDays} calendar days, the first day counted as day one; ${ic.investigationDeadlineBasis === 'first_knowledge' ? 'from the supervisor\'s recorded first knowledge' : 'from the notice of charge - the true deadline is on or before this date where the agreement counts from first knowledge'}).`
              : ' No notice window is recorded on this platform for this agreement - check the controlling agreement and ask your General Chairperson.')
          + (ic.intentMeetingDue ? ` Intent meeting due by ${ic.intentMeetingDue} (from the employee's receipt of the letter of intent).` : '')
          + (ic.intentBarFlag ? ' ' + ic.intentBarFlag : ''),
        // Record 592 (functions audit F21): signed by the person who opened it. The rules now refuse a client-written
        // "System" note (any party could forge one); the text already names him.
        authorName: user?.name || user?.email || 'Unknown',
        authorRole: uiRole(user),
        timestamp: serverTimestamp(),
      });

      if (onSaved) onSaved(caseRef.id);
    } catch (e) {
      console.error('Error creating discipline case:', e);
      // F-22: say what happened. Since F15 (ii), an upload failure leaves a DRAFT (its author's alone); anything else names its code.
      alert(e && (e.ycpStage === 'upload' || e.ycpStage === 'open') ? e.message : ('The case could not be completed (' + ((e && e.code) || (e && e.message) || 'unknown error') + '). Check the case list before trying again - a case written before this error stands.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#F4F6F8' }}>New Discipline Case</div>
          {isTesterAccount(user) && (   /* Record 613 (Bob 10/8), F22 */
            <div style={{ marginTop: 6, padding: '4px 10px', border: '2px solid #E8A020', borderRadius: 4, color: '#E8A020', fontWeight: 800, fontSize: 12, letterSpacing: 1 }}>
              {EXAMPLE_MARK} - this is a test account. The case is filed in the test territory {TEST_SCOPE}, marked {EXAMPLE_MARK}, with
              your own e-mail as the employee's. It never appears in a real officer's list and is never given a real case number.
            </div>
          )}
          <div style={{ fontSize: 12, color: '#8AA4BF', marginTop: 2 }}>
            Three fields are required: the employee, the type of discipline, and the date on the notice of charge or on the
            letter of intent. Each deadline is counted from the dates recorded below, the first day as day one.
          </div>
        </div>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>

      {/* Member Information */}
      <CollapseSectionLocal title="Charged Employee" icon="👤" color="#4A9EE8" defaultOpen={true}>
        <div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Employee Name *</label>
            <input aria-label="Employee Name" className="form-input" value={form.memberName} onChange={e => set('memberName', e.target.value)} placeholder="Full legal name" />
          </div>
          <div className="form-group">
            <label className="form-label">Type of Discipline *</label>
            <input aria-label="Type of Discipline" className="form-input" value={form.disciplineType}
              onChange={e => set('disciplineType', e.target.value)}
              list="discipline-type-options"
              placeholder="e.g. Investigation, Formal Hearing, Waiver offered" />
            <datalist id="discipline-type-options">
              {DISCIPLINE_TYPES.map(t => <option key={t} value={t} />)}
            </datalist>
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              Filled in for you if a charging letter is read below; you can always type it yourself.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Date on the Notice of Charge *</label>
            <input aria-label="Date on the Notice of Charge" className="form-input" type="date" value={form.noticeDate}
              onChange={e => set('noticeDate', e.target.value)} />
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              * This date or the date on a letter of intent (below). Where the agreement counts the hearing from the supervisor's first knowledge, record that date too - otherwise the deadline is counted from this date and shown as a latest bound. [R-158]
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Date the employee received the notice of charge</label>
            <input aria-label="Date the employee received the notice of charge" className="form-input" type="date" value={form.chargeReceivedOn}
              onChange={e => set('chargeReceivedOn', e.target.value)} />
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              Receipt is a fact - hand delivered and signed for, e-mailed with proof of delivery, or certified mail - never assumed from the date on the letter.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Supervisor's first knowledge of the matter</label>
            <input aria-label="Supervisor's first knowledge of the matter" className="form-input" type="date" value={form.firstKnowledgeOn}
              onChange={e => set('firstKnowledgeOn', e.target.value)} />
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              The day the carrier discovered it - the report received, or its official's own observation. That day is day one of the time limits. Leave blank if no letter or record states it.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Date on the letter of intent to impose discipline</label>
            <input aria-label="Date on the letter of intent to impose discipline" className="form-input" type="date" value={form.intentLetterDate}
              onChange={e => set('intentLetterDate', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Date the employee received the letter of intent</label>
            <input aria-label="Date the employee received the letter of intent" className="form-input" type="date" value={form.intentReceivedOn}
              onChange={e => set('intentReceivedOn', e.target.value)} />
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              The intent meeting is counted from this day - the day of receipt is day one. It is not the hearing and not a prehearing meeting; prepare for it as though everything hangs on it.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">The letter of intent itself</label>
            <input aria-label="The letter of intent itself" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setIntentFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              Stored as the letter of intent - a separate letter from the charging letter below, with its own meeting.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input aria-label="Email" className="form-input" value={form.memberEmail} onChange={e => set('memberEmail', e.target.value)} placeholder="Employee email" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Craft *</label>
            <select aria-label="Craft" className="form-select" value={form.craft} onChange={e => {
              const cf = e.target.value;
              set('craft', cf);
              // A7: the railroad options are RAILROADS[craft]. Changing craft used to leave the OLD
              // railroad in form.railroad - the select rendered as though nothing was chosen, and the
              // stale value was what saved. That produced a case whose craft/railroad pair exists in
              // no agreement, so no rule could ever match it. Keep the road when the new craft really
              // works it; drop it when it does not.
              const stillOffered = (RAILROADS[cf] || []).includes(form.railroad);
              const rr = stillOffered ? form.railroad : '';
              if (!stillOffered) set('railroad', '');
              const gcx = COMMITTEE_LOOKUP[cf]?.[rr] || '';
              set('generalCommittee', gcx);   // A5: empty when the lookup misses, never a guessed committee
              set('applicableAgreement', AGREEMENT_LOOKUP[gcx]?.[cf]?.[rr] || '');
              set('workLocation', ''); set('scopeKey', '');
            }}>
              <option value="">Select craft...</option>
              {CRAFTS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Railroad / Employer *</label>
            <select aria-label="Railroad / Employer" className="form-select" value={form.railroad} onChange={e => {
              const rr = e.target.value;
              set('railroad', rr);
              const gcx = COMMITTEE_LOOKUP[form.craft || '']?.[rr] || ''; set('generalCommittee', gcx);   /* A5: empty when the lookup misses, never a guessed committee */ const ag = AGREEMENT_LOOKUP[gcx]?.[form.craft || '']?.[rr] || '';
              set('applicableAgreement', ag);
              set('workLocation', ''); set('scopeKey', '');
            }}>
              <option value="">Select railroad...</option>
              {(RAILROADS[form.craft] || []).map(rr => <option key={rr} value={rr}>{rr}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">General Committee</label>
            {/* A6. This was a free-text box. generalCommittee is what every committee-scoped query
                matches on, so a typo produced a case readable by nobody - including the General
                Chairman whose case it is. routing.js already owns the rule: the committee is a
                PROJECTION, never an independent field. It is shown here, not typed. */}
            {form.generalCommittee ? (
              <div className="form-input" style={{ background: '#0A1628', color: '#D8E2EE', display: 'flex', alignItems: 'center' }}>
                {form.generalCommittee}
              </div>
            ) : (
              <div className="form-input" style={{ background: '#2D1F0A', border: '1px solid #E8A020', color: '#FFD27A', display: 'flex', alignItems: 'center' }}>
                No committee resolves
              </div>
            )}
            <div style={{ fontSize: 11, color: '#6F8AA6', marginTop: 4 }}>
              {form.generalCommittee
                ? 'Taken from the craft and railroad above. It is not typed, because a committee that does not exactly match is a case nobody can open.'
                : 'The craft and railroad above resolve to no General Committee on record. The case can still be written, and it will carry no committee rather than a guessed one — a wrong committee is worse than none, because nobody catches it.'}
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Work Location (names the Local Chairperson)</label>
            <select aria-label="Work Location (names the Local Chairperson)" className="form-select" value={form.workLocation} onChange={e => {
              const loc = e.target.value;
              set('workLocation', loc);
              set('scopeKey', resolveScopeKey(form.craft, form.railroad, loc));
            }}>
              <option value="">Select work location...</option>
              {(LOCATIONS_BY_RAILROAD[(form.craft ? form.craft + "\u241F" : "") + form.railroad] || LOCATIONS_BY_RAILROAD[form.railroad] || []).map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
            <div style={{ fontSize: 11, color: form.scopeKey ? '#40C070' : '#8AA4BF', marginTop: 4 }}>
              {form.scopeKey ? '\u2713 Local Chairperson territory: ' + form.scopeKey : <>Until a work location is chosen, no Local Chairperson can see this case.<RulingRef user={user} n={100} why="A Local Chairperson sees the cases of his own territory, and the territory is keyed by work location." /></>}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Applicable Agreement</label>
          {form.applicableAgreement ? (
            <div style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#40C070', marginBottom: 2 }}>✓ Auto-identified from Railroad</div>
              <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{form.applicableAgreement}</div>
            </div>
          ) : (
            <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '10px 14px', color: 'var(--steel)', fontSize: 13 }}>
              Select Railroad above to identify agreement
            </div>
          )}
        </div><HowToPanel kind="discipline" agreement={form.applicableAgreement} audience="officer" />{/* Bob 2026-10-15; on this line so no cited line moves */}
      </div>
      </CollapseSectionLocal>

      {/* Discipline Notice */}
      <CollapseSectionLocal title="Discipline Notice & AI Extraction" icon="⚖️" color="#E8A020" defaultOpen={true}>
        <div>

        {/* Charging Letter Upload + AI 4-paragraph extraction */}
        <div className="form-group">
          <div style={{ background: '#1A1400', border: '2px dashed #C9A84C', borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#E8A020', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
              ★ Original Charging Letter
            </div>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 12 }}>
              Upload the carrier charging letter. The platform reads out the letter's origin, who it is addressed to, and the investigation details. Whether the letter carries a procedural defect is the officer's call, not the platform's.
            </div>
            <input aria-label="Attach a document to this case" type="file" key={fileInputKey} accept=".pdf,.jpg,.jpeg,.png"
              onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                setNoticeFile(file);
                setExtractingLetter(true);
                set('chargesDescription', '⚙ Reading charging letter... extracting information...');
                try {
                  const reader = new FileReader();
                  reader.onload = async (ev) => {
                    const b64 = ev.target.result.split(',')[1];
                    const mediaType = file.type || 'application/pdf';
                    setNoticeFileB64(b64);
                    setNoticeFileType(mediaType);
                    // Use Cloud Function with base64 document
                    const response = await fetch(ANALYZE_URL, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
                      body: JSON.stringify({
                        mode: 'discipline_letter',
                        agreement: form.applicableAgreement || 'railroad labor agreement',
                        description: 'Extract charging letter information',
                        documentBase64: b64,
                        documentMediaType: mediaType
                      })
                    });
                    const data = await response.json();
                    const extracted = data.result || '';
                    if (!extracted && data.error) {
                      set('chargesDescription', 'Automatic extraction failed: ' + data.error + '\n\nPlease enter the information manually below.');
                      setExtractingLetter(false);
                    } else if (extracted) {
                      set('chargesDescription', extracted);
                      set('disciplineType', 'As Stated in Charging Letter');
                      setExtractingLetter(false);
                      // Auto-generate the full defense package from the fresh extraction (sequential)
                      (async () => {
                        const q = await runQuestions(extracted);
                        const overrides = { chargesDescription: extracted, proceduralObjections: form.proceduralObjections || '', investigationQuestions: q };
                        await runExhibits(overrides);
                        await runCase(overrides);
                      })();
                    } else {
                      set('chargesDescription', 'Could not extract text automatically. Please enter the information manually below.');
                      setExtractingLetter(false);
                    }
                  };
                  reader.readAsDataURL(file);
                } catch(err) {
                  set('chargesDescription', 'Upload succeeded. Could not read automatically — please enter information manually below.');
                  setExtractingLetter(false);
                }
              }}
              style={{ color: '#F4F6F8' }}
            />
            {noticeFile && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: '#40C070' }}>
                  ✓ {noticeFile.name} — will be pinned as Original Charging Letter
                </div>
                <button type="button"
                  onClick={() => {
                    setNoticeFile(null);
                    setNoticeFileB64(null);
                    setNoticeFileType(null);
                    set('chargesDescription', '');
                    set('proceduralObjections', '');
                    set('investigationQuestions', '');
                    setExtractingLetter(false);
                    setFileInputKey(k => k + 1);
                  }}
                  style={{ marginTop: 10, background: 'transparent', border: '1px solid #C0453C', color: '#E06A60', fontSize: 11, fontWeight: 700, padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>
                  ✕ Remove file & choose another
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Extracted record — formal document view with edit toggle */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label className="form-label" style={{ margin: 0 }}>Extracted Record — Charging Letter</label>
            {form.chargesDescription ? (
              <button type="button"
                onClick={() => setEditingExtract(v => !v)}
                style={{ background: 'transparent', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>
                {editingExtract ? '✓ Done' : '✎ Edit'}
              </button>
            ) : null}
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 8 }}>
            {editingExtract ? 'Edit the text below, then tap Done to return to the formatted record.' : 'Auto-filled when you upload the charging letter above. Tap Edit to make changes.'}
          </div>

          {(editingExtract || !form.chargesDescription) ? (
            <textarea aria-label="Charges description" className="form-textarea" rows={16} value={form.chargesDescription}
              onChange={e => set('chargesDescription', e.target.value)}
              placeholder="Upload the charging letter above — information will appear here automatically.&#10;&#10;Or enter manually:&#10;&#10;DATE OF LETTER:&#10;&#10;ADDRESSEE (Charged Employee):&#10;&#10;ADDRESSOR (Issuing Party):&#10;&#10;HEARING / INVESTIGATION SCHEDULE:&#10;&#10;THE CHARGE(S) — VERBATIM:&#10;&#10;SPECIFICATION:&#10;&#10;CHARGING OFFICER:&#10;&#10;HEARING OFFICER:&#10;&#10;WITNESSES:&#10;&#10;ADDITIONAL PERSONS:"
            />
          ) : (
            <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '22px 24px' }}>
              <div style={{ textAlign: 'center', fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#C9A84C', borderBottom: '2px solid rgba(201,168,76,0.5)', paddingBottom: 12, marginBottom: 18 }}>
                Extracted Record · Original Charging Letter
              </div>
              {renderExtractedRecord(form.chargesDescription)}
            </div>
          )}
        </div>



        {/* Procedural Objections */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Procedural Objections</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {editingObjections && form.proceduralObjections && !form.proceduralObjections.startsWith('⚙') && (
              <button type="button" onClick={() => setEditingObjections(false)}
                style={{ background: 'transparent', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>
                ✓ Done
              </button>
            )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
            {editingObjections ? 'Edit the text below, then tap Done to return to the formatted brief.' : 'Written by the officer. State each objection and cite only a catalogued provision, verbatim. The platform does not generate objections. [R-201]'}
          </div>
          {(editingObjections || !form.proceduralObjections || form.proceduralObjections.startsWith('⚙') || form.proceduralObjections.startsWith('Error') || form.proceduralObjections.startsWith('Could not')) ? (
            <textarea aria-label="Enter each procedural objection yourself... [R-201]" className="form-textarea" rows={8} value={form.proceduralObjections}
              onChange={e => set('proceduralObjections', e.target.value)}
              placeholder="Enter each procedural objection yourself... [R-201]" />
          ) : (
            <div style={{ position: 'relative' }}>
              <button type="button"
                onClick={() => setEditingObjections(true)}
                style={{ position: 'absolute', top: 12, right: 14, zIndex: 2, background: '#1B2432', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                ✎ Edit
              </button>
              <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '22px 24px' }}>
                {renderFormalDoc(form.proceduralObjections)}
                <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid rgba(201,168,76,0.25)', fontSize: 10.5, color: '#7A8794', fontStyle: 'normal', lineHeight: 1.5 }}>
                  Preparation aid for the union representative — review and verify all citations against the controlling agreement and current arbitration authority before use. Not legal advice.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Investigation Questions */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Investigation Questions</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {editingQuestions && form.investigationQuestions && !form.investigationQuestions.startsWith('⚙') && (
              <button type="button" onClick={() => setEditingQuestions(false)}
                style={{ background: 'transparent', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>
                ✓ Done
              </button>
            )}
            {extractingQuestions && (
              <button type="button" onClick={() => cancelGen('investigationQuestions')} style={{ background: 'transparent', border: '1px solid var(--steel)', color: '#8AA4BF', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>✕ Cancel</button>
            )}
            {!extractingQuestions && restorable.investigationQuestions && (
              <button type="button" onClick={() => restoreGen('investigationQuestions')} style={{ background: 'transparent', border: '1px solid var(--steel)', color: '#8AA4BF', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>↺ Restore previous</button>
            )}
            {noticeFileB64 && (
              <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }}
                disabled={extractingQuestions || extractingLetter || !form.chargesDescription || form.chargesDescription.startsWith('⚙')}
                onClick={() => runQuestions()}>
                {extractingQuestions ? '⚙ Generating...' : (form.investigationQuestions && !form.investigationQuestions.startsWith('Error') && !form.investigationQuestions.startsWith('Could not') ? '↻ Regenerate' : '⚙ Generate from Letter')}
              </button>
            )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
            {editingQuestions ? 'Edit the text below, then tap Done to return to the formatted list.' : 'Auto-generated from the charging letter — organized by section.'}
          </div>
          {(editingQuestions || !form.investigationQuestions || form.investigationQuestions.startsWith('⚙') || form.investigationQuestions.startsWith('Error') || form.investigationQuestions.startsWith('Could not')) ? (
            <textarea aria-label="Upload the charging letter above then click" className="form-textarea" rows={10} value={form.investigationQuestions}
              onChange={e => set('investigationQuestions', e.target.value)}
              placeholder="Upload the charging letter above then click 'Generate from Letter' — or enter questions manually..." />
          ) : (
            <div style={{ position: 'relative' }}>
              <button type="button"
                onClick={() => setEditingQuestions(true)}
                style={{ position: 'absolute', top: 12, right: 14, zIndex: 2, background: '#1B2432', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                ✎ Edit
              </button>
              <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '22px 24px' }}>
                {renderFormalDoc(form.investigationQuestions)}
                <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid rgba(201,168,76,0.25)', fontSize: 10.5, color: '#7A8794', lineHeight: 1.5 }}>
                  Preparation aid for the union representative — review and adapt to the facts and controlling agreement before the hearing. Not legal advice.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Box 1 — Supporting Board Awards / Precedent */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Supporting Board Awards / Precedent</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {editingPrecedent && form.precedentResearch && !form.precedentResearch.startsWith('⚙') && (
                <button type="button" onClick={() => setEditingPrecedent(false)}
                  style={{ background: 'transparent', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>
                  ✓ Done
                </button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 8 }}>
            Entered by the officer. Web research is never authority: an award belongs here only once you have retrieved it and checked it against its own text. [R-201]
          </div>

          {form.precedentResearch ? (
            (editingPrecedent || form.precedentResearch.startsWith('⚙') || form.precedentResearch.startsWith('Error') || form.precedentResearch.startsWith('Could not')) ? (
              <textarea aria-label="Precedent research" className="form-textarea" rows={8} style={{ marginBottom: 12 }} value={form.precedentResearch}
                onChange={e => set('precedentResearch', e.target.value)} />
            ) : (
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <button type="button" onClick={() => setEditingPrecedent(true)}
                  style={{ position: 'absolute', top: 12, right: 14, zIndex: 2, background: '#1B2432', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                  ✎ Edit
                </button>
                <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '22px 24px' }}>
                  {renderFormalDoc(form.precedentResearch)}
                  <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid rgba(201,168,76,0.25)', fontSize: 10.5, color: '#7A8794', lineHeight: 1.5 }}>
                    Web-researched — open each source link and confirm the award before relying on it. Not legal advice.
                  </div>
                </div>
              </div>
            )
          ) : null}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#8AA4BF' }}>You can also add awards you've located yourself:</div>
            <div style={{ position: 'relative', display: 'inline-block' }}
              onMouseEnter={e => e.currentTarget.querySelector('.award-tip').style.display = 'block'}
              onMouseLeave={e => e.currentTarget.querySelector('.award-tip').style.display = 'none'}>
              <span style={{ fontSize: 11, color: '#4A9EE8', cursor: 'pointer', fontWeight: 700, border: '1px solid #4A9EE8', borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>?</span>
              <div className="award-tip" style={{ display: 'none', position: 'absolute', left: 22, top: -6, zIndex: 200, background: '#0D1B2A', border: '1px solid #4A9EE8', borderRadius: 8, padding: '12px 14px', width: 290, boxShadow: '0 4px 20px rgba(0,0,0,0.6)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#4A9EE8', marginBottom: 6 }}>📋 Purpose & How to Use</div>
                <div style={{ fontSize: 11, color: '#C8D8E8', lineHeight: 1.7 }}>
                  The auto-research above finds web-available awards. But many of the most powerful NRAB and PLB awards are not publicly indexed — they live in union files, GC archives, or physical binders.
                  <br /><br />
                  <span style={{ color: '#E8A020', fontWeight: 700 }}>How to use:</span> When you know of a relevant award from your own collection, click <span style={{ color: '#F4F6F8', fontWeight: 700 }}>+ Add Award</span> and enter the board type, award number, how it supports the defense, and a link if available.
                  <br /><br />
                  <span style={{ color: '#40C070', fontWeight: 700 }}>Auto-research catches what is online. Manual entry catches what is in the drawer.</span> Together they build a complete precedent record for the case.
                </div>
              </div>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '18px 20px' }}>
            {(form.boardAwards || []).length === 0 ? (
              <div style={{ color: '#7A8794', fontSize: 13, marginBottom: 14, fontFamily: 'Georgia, "Times New Roman", serif' }}>No awards cited yet.</div>
            ) : (
              (form.boardAwards || []).map((a, i) => (
                <div key={i} style={{ borderLeft: '3px solid #C9A84C', paddingLeft: 14, marginBottom: 14 }}>
                  <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, fontWeight: 700, color: '#E8A020' }}>{a.boardType} Award #{a.awardNumber}{a.date ? ' — ' + a.date : ''}</div>
                  <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, color: '#DDE3E8', lineHeight: 1.6, marginTop: 2 }}>{a.summary}</div>
                  {a.link ? <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#4A9EE8' }}>→ View award</a> : null}
                  <button type="button" onClick={() => set('boardAwards', (form.boardAwards || []).filter((_, j) => j !== i))} style={{ display: 'block', marginTop: 6, background: 'transparent', border: 'none', color: '#E06A60', fontSize: 11, cursor: 'pointer', padding: 0 }}>Remove</button>
                </div>
              ))
            )}
            <div style={{ borderTop: '1px solid rgba(201,168,76,0.25)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select aria-label="Board type" className="form-select" style={{ fontSize: 12, flex: '0 0 120px' }} value={newAward.boardType} onChange={e => setNewAward(p => ({ ...p, boardType: e.target.value }))}>
                  <option>PLB</option>
                  <option>SBA</option>
                  <option>NRAB</option>
                  <option>4th Division</option>
                  <option>Public Law Board</option>
                </select>
                <input aria-label="Award #" className="form-input" style={{ fontSize: 12, flex: 1 }} placeholder="Award #" value={newAward.awardNumber} onChange={e => setNewAward(p => ({ ...p, awardNumber: e.target.value }))} />
                <input aria-label="Date (opt.)" className="form-input" style={{ fontSize: 12, flex: '0 0 120px' }} placeholder="Date (opt.)" value={newAward.date} onChange={e => setNewAward(p => ({ ...p, date: e.target.value }))} />
              </div>
              <input aria-label="How this award supports the defense" className="form-input" style={{ fontSize: 12, marginBottom: 8, width: '100%' }} placeholder="How this award supports the defense" value={newAward.summary} onChange={e => setNewAward(p => ({ ...p, summary: e.target.value }))} />
              <input aria-label="Link to award (optional)" className="form-input" style={{ fontSize: 12, marginBottom: 8, width: '100%' }} placeholder="Link to award (optional)" value={newAward.link} onChange={e => setNewAward(p => ({ ...p, link: e.target.value }))} />
              <button type="button" className="btn btn-secondary" style={{ fontSize: 11 }}
                disabled={!newAward.awardNumber || !newAward.summary}
                onClick={() => { set('boardAwards', [...(form.boardAwards || []), newAward]); setNewAward({ boardType: 'PLB', awardNumber: '', date: '', summary: '', link: '' }); }}>
                + Add Award
              </button>
            </div>
          </div>
        </div>

        {/* Box 2 — Exhibits */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Exhibits</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {editingExhibits && form.exhibitList && !form.exhibitList.startsWith('⚙') && (
                <button type="button" onClick={() => setEditingExhibits(false)}
                  style={{ background: 'transparent', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>
                  ✓ Done
                </button>
              )}
              {extractingExhibits && (
                <button type="button" onClick={() => cancelGen('exhibitList')} style={{ background: 'transparent', border: '1px solid var(--steel)', color: '#8AA4BF', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>✕ Cancel</button>
              )}
              {!extractingExhibits && restorable.exhibitList && (
                <button type="button" onClick={() => restoreGen('exhibitList')} style={{ background: 'transparent', border: '1px solid var(--steel)', color: '#8AA4BF', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>↺ Restore previous</button>
              )}
              <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }}
                disabled={extractingExhibits || !form.chargesDescription || form.chargesDescription.startsWith('⚙')}
                onClick={() => runExhibits()}>
                {extractingExhibits ? '⚙ Generating...' : (form.exhibitList && !form.exhibitList.startsWith('Error') && !form.exhibitList.startsWith('Could not') ? '↻ Regenerate' : '⚙ Generate Exhibit List')}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
            {editingExhibits ? 'Edit the text below, then tap Done to return to the formatted list.' : 'Built from the boxes above — the evidence to gather, demand, or introduce for the member.'}
          </div>
          {(editingExhibits || !form.exhibitList || form.exhibitList.startsWith('⚙') || form.exhibitList.startsWith('Error') || form.exhibitList.startsWith('Could not')) ? (
            <textarea aria-label="Tap" className="form-textarea" rows={8} value={form.exhibitList}
              onChange={e => set('exhibitList', e.target.value)}
              placeholder="Tap 'Generate Exhibit List' once the boxes above are filled — or enter exhibits manually..." />
          ) : (
            <div style={{ position: 'relative' }}>
              <button type="button" onClick={() => setEditingExhibits(true)}
                style={{ position: 'absolute', top: 12, right: 14, zIndex: 2, background: '#1B2432', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                ✎ Edit
              </button>
              <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '22px 24px' }}>
                {renderFormalDoc(form.exhibitList)}
                <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid rgba(201,168,76,0.25)', fontSize: 10.5, color: '#7A8794', lineHeight: 1.5 }}>
                  Preparation aid — confirm each exhibit exists and is obtainable before relying on it. Not legal advice.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Box 3 — Defense Case File for the Local Chairperson */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className="form-label" style={{ marginBottom: 0 }}>Defense Case File — For the Local Chairperson</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {editingCase && form.casePresentation && !form.casePresentation.startsWith('⚙') && (
                <button type="button" onClick={() => setEditingCase(false)}
                  style={{ background: 'transparent', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '4px 10px', borderRadius: 5, cursor: 'pointer' }}>
                  ✓ Done
                </button>
              )}
              <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }}
                disabled={extractingCase || !form.chargesDescription || form.chargesDescription.startsWith('⚙')}
                onClick={() => runCase()}>
                {extractingCase ? '⚙ Building...' : (form.casePresentation && !form.casePresentation.startsWith('Error') && !form.casePresentation.startsWith('Could not') ? '↻ Rebuild' : '⚙ Build Case File')}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
            {editingCase ? 'Edit the text below, then tap Done to return to the formatted file.' : "The member's case organized top-to-bottom so the LC can read it and present it in defense."}
          </div>
          {(editingCase || !form.casePresentation || form.casePresentation.startsWith('⚙') || form.casePresentation.startsWith('Error') || form.casePresentation.startsWith('Could not')) ? (
            <textarea aria-label="Tap" className="form-textarea" rows={10} value={form.casePresentation}
              onChange={e => set('casePresentation', e.target.value)}
              placeholder="Tap 'Build Case File' once the boxes above are filled — or write the presentation manually..." />
          ) : (
            <div style={{ position: 'relative' }}>
              <button type="button" onClick={() => setEditingCase(true)}
                style={{ position: 'absolute', top: 12, right: 14, zIndex: 2, background: '#1B2432', border: '1px solid #C9A84C', color: '#E8A020', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
                ✎ Edit
              </button>
              <div style={{ background: 'linear-gradient(180deg,#12171D,#0E1318)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, padding: '22px 24px' }}>
                {renderFormalDoc(form.casePresentation)}
                <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid rgba(201,168,76,0.25)', fontSize: 10.5, color: '#7A8794', lineHeight: 1.5 }}>
                  Preparation aid for the union representative — review and adapt to the facts and controlling agreement before the hearing. Not legal advice.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* REQUEST FOR PRODUCTION OF WITNESSES */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Request for Production of Witnesses</div>
          <div className="card-subtitle">Formal demand to the carrier to produce witnesses at the investigation hearing</div>
        </div>
        <div className="form-group">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#8AA4BF" }}>Fill in witness names, titles, and reasons after generating the template.</div>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: "3px 10px" }}
              onClick={() => {
                const ag = form.applicableAgreement || "the applicable agreement";
                const rr = form.railroad || "the Carrier";
                const gc = form.generalCommittee || "[GENERAL COMMITTEE]";   // A5: the template's own idiom for an unknown, not another committee's name on a letter to the Carrier
                const tmpl = `${PARENT_ORGANIZATION}\n\nREQUEST FOR PRODUCTION OF WITNESSES\n\nDate: [DATE]\n\nTo: [HEARING OFFICER NAME AND TITLE]\n${rr}\n[ADDRESS]\n\nRe: Investigation Hearing — [EMPLOYEE NAME]\n\nPursuant to ${ag}, the Organization formally requests production of the following witnesses at the investigation scheduled for [DATE AND TIME]:\n\n1. [WITNESS NAME] — [TITLE]\n   Basis: [WHY THIS WITNESS HAS RELEVANT KNOWLEDGE]\n\n2. [WITNESS NAME] — [TITLE]\n   Basis: [WHY THIS WITNESS HAS RELEVANT KNOWLEDGE]\n\n3. [WITNESS NAME] — [TITLE]\n   Basis: [WHY THIS WITNESS HAS RELEVANT KNOWLEDGE]\n\nThe Organization reserves the right to call additional witnesses. Please confirm availability in advance.\n\nRespectfully submitted,\n\n_______________________________\n[LC NAME], Local Chairperson\nSMART-TD ${gc}`;
                set("witnessRequest", tmpl);
              }}>
              ⚙ Generate Template
            </button>
          </div>
          <textarea aria-label="Witness request" className="form-textarea" rows={10} value={form.witnessRequest || ""}
            onChange={e => set("witnessRequest", e.target.value)}
            placeholder="Click Generate Template above — then fill in witness names, titles, and reasons..." />
        </div>
      </div>

      {/* REQUEST FOR PRODUCTION OF EVIDENCE */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Request for Production of Evidence</div>
          <div className="card-subtitle">Formal demand to the carrier for specific items of evidence before or at the investigation</div>
        </div>
        <div className="form-group">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#8AA4BF" }}>Fill in the specific items of evidence you are demanding from the carrier.</div>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 11, padding: "3px 10px" }}
              onClick={() => {
                const ag = form.applicableAgreement || "the applicable agreement";
                const rr = form.railroad || "the Carrier";
                const gc = form.generalCommittee || "[GENERAL COMMITTEE]";   // A5: the template's own idiom for an unknown, not another committee's name on a letter to the Carrier
                const tmpl = `${PARENT_ORGANIZATION}\n\nREQUEST FOR PRODUCTION OF EVIDENCE\n\nDate: [DATE]\n\nTo: [HEARING OFFICER NAME AND TITLE]\n${rr}\n[ADDRESS]\n\nRe: Investigation Hearing — [EMPLOYEE NAME]\n\nPursuant to ${ag}, the Organization formally demands production of the following items prior to or at the investigation scheduled for [DATE AND TIME]:\n\n1. [ITEM — e.g., All video surveillance footage from [LOCATION] on [DATE]]\n\n2. [ITEM — e.g., All written statements, reports, or memoranda relating to this incident]\n\n3. [ITEM — e.g., Complete personnel and discipline history of the charged employee]\n\n4. [ITEM — e.g., All crew management system records, sign-up sheets, and assignment logs for [DATE]]\n\n5. [ITEM — e.g., Names and statements of all witnesses interviewed by the Carrier]\n\nFailure to produce the requested evidence may result in a postponement request and shall be noted as a procedural objection in the hearing record.\n\nRespectfully submitted,\n\n_______________________________\n[LC NAME], Local Chairperson\nSMART-TD ${gc}`;
                set("evidenceRequest", tmpl);
              }}>
              ⚙ Generate Template
            </button>
          </div>
          <textarea aria-label="Evidence request" className="form-textarea" rows={10} value={form.evidenceRequest || ""}
            onChange={e => set("evidenceRequest", e.target.value)}
            placeholder="Click Generate Template above — then fill in the specific items of evidence you are demanding..." />
        </div>
      </div>

      </CollapseSectionLocal>
      {lastSaved && (
        <div style={{ fontSize: 10, color: '#40C070', textAlign: 'right', marginBottom: 4 }}>
          ✓ Draft saved {lastSaved}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <button className="btn btn-secondary" onClick={onCancel}>← Back</button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" disabled={savingDraft}
            onClick={async () => {
              setSavingDraft(true);
              try {
                const ex613 = exampleStamp(user);   // Record 613 (Bob 10/8), F22: a tester's draft is an example too
                if (!ex613.ok) { window.alert(ex613.why); return; }
                const draftData = { ...form, ...ex613.stamp, status: 'draft', openedBy: user?.email || '', updatedAt: serverTimestamp(),
                  createdBy: (user && user.uid) || (auth.currentUser && auth.currentUser.uid) || '' };  // SS172
                if (draftId) {
                  await updateDoc(doc(db, 'disciplineCases', draftId), logAudit(draftData, user?.email || auth.currentUser?.email, 'Draft updated'));
                } else {
                  const ref = await addDoc(collection(db, 'disciplineCases'), logAudit({ ...draftData, openedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'Draft created'));
                  setDraftId(ref.id);
                }
                setLastSaved(new Date().toLocaleTimeString());
              } catch(e) { console.error('Draft save failed:', e); window.alert('Draft NOT saved: ' + ((e && e.message) || 'the write was refused') + '. Your entries are still on screen.'); }   // Record 586 (F8): never silent
              finally { setSavingDraft(false); }
            }}>
            {savingDraft ? '⚙ Saving...' : '💾 Save Draft'}
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Opening Case...' : '⚖️ Open Discipline Case'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DISCIPLINE CASE DETAIL ──────────────────────────────────────────────────
function DisciplineCaseDetail({ caseId, user, onBack }) {
  const [disciplineCase, setDisciplineCase] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('Other');
  const [hearingUploadFile, setHearingUploadFile] = useState(null);
  const [hearingUploadType, setHearingUploadType] = useState('Investigation Transcript');
  const [appealUploadFile, setAppealUploadFile] = useState(null);
  const [appealUploadType, setAppealUploadType] = useState('Appeal Letter');
  const [uploading, setUploading] = useState(false);
  const [showHearingForm, setShowHearingForm] = useState(false);
  const [hearingLog, setHearingLog] = useState({
    hearingDate: '', hearingTime: '', location: '',
    carriersPresent: '', unionRepsPresent: '',
    memberPresent: true, hearingOfficer: '',
    memberPlea: 'Not Guilty', postponements: 0,
    witnesses: '', transcriptNotes: '',
  });
  const [lcAssessment, setLcAssessment] = useState({
    strengthRating: '', recommendation: '',
    stepNotes: '', strategyNotes: '',
  });
  // Export individual box to PDF (print-formatted popup)
  const exportBox = (title, content) => {
    const caseHeader = `
      <div style="font-family: Arial, sans-serif; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 2px solid #E8A020;">
        <div style="font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 1px;">${PLATFORM_NAME} — Discipline Case</div>
        ${isExampleCase(disciplineCase) ? '<div style="display: inline-block; border: 2px solid #B8860B; color: #B8860B; font-weight: bold; letter-spacing: 2px; padding: 2px 8px; margin: 4px 0;">' + EXAMPLE_MARK + ' - not a real case</div>' : ''}
        <div style="font-size: 16px; font-weight: bold; margin: 4px 0;">${disciplineCase?.caseNumber || 'Case Record'}</div>
        <div style="font-size: 12px; color: #444;">${disciplineCase?.memberName || ''} · ${disciplineCase?.generalCommittee || ''} · ${disciplineCase?.railroad || ''}</div>
        <div style="font-size: 11px; color: #666;">Agreement: ${disciplineCase?.applicableAgreement || ''}</div>
        <div style="font-size: 11px; color: #666;">Exported: ${new Date().toLocaleDateString('en-US', {weekday:'long', year:'numeric', month:'long', day:'numeric'})}</div>
      </div>
      <div style="font-size: 15px; font-weight: bold; color: #1a3a5c; margin-bottom: 12px;">${title}</div>
    `;
    const html = `
      <html><head><title>${title} — ${disciplineCase?.caseNumber}</title>
      <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; color: #222; line-height: 1.6; }
        @media print { body { margin: 20px; } }
        pre { white-space: pre-wrap; font-family: Arial, sans-serif; font-size: 12px; }
        h2 { color: #1a3a5c; }
      </style></head>
      <body>
        ${caseHeader}
        <pre>${content.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre>
      </body></html>
    `;

    // 2026-09-25. THIS USED TO BE `window.open('', '_blank')` AND IT HAD TWO FAULTS.
    //
    // 1. The returned window was never checked. A blocked popup makes it null, the next line throws,
    //    and the officer gets NOTHING - no file, no error, no explanation. He clicks and the button
    //    does nothing at all, which is the worst way for anything here to fail (PD-4).
    // 2. Officers reach this platform through an Edge --app= shortcut, and in app mode a popup does
    //    not open a tab - it opens a WHOLE REGULAR BROWSER WINDOW, which on a default Edge lands on
    //    the new-tab page first. Printing a case should not put a search engine on the screen.
    //
    // So nothing is opened. The document is written into a hidden iframe and printed from there -
    // no window, nothing for a popup blocker to refuse, and the same Print / Save as PDF dialog.
    // If even that fails, the file is offered as a download rather than the click doing nothing.
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('title', title + ' (print)');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
    const cleanUp = () => { try { frame.remove(); } catch (e) { /* already gone */ } };
    const fallbackDownload = (why) => {
      cleanUp();
      try {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (String(disciplineCase?.caseNumber || 'case') + ' - ' + title).replace(/[\\/:*?"<>|]/g, '-') + '.html';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } catch (e) {
        // `alert` and not a state setter: this component carries no error state of its own, and the
        // one thing that must never happen here is the click doing nothing. `alert` is already this
        // file's idiom for an unmissable message (the "Still needed:" check does the same).
        window.alert('That section could not be printed or saved (' +
          (why || (e && e.message) || 'unknown') + '). Nothing was changed on the case.');
      }
    };
    try {
      document.body.appendChild(frame);
      const doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document);
      if (!doc) { fallbackDownload('the print frame could not be created'); return; }
      doc.open();
      doc.write(html);
      doc.close();
      // The frame must be laid out before print(), or an empty page goes to the printer.
      setTimeout(() => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
          // Long enough that the print dialog owns the document before the frame goes.
          setTimeout(cleanUp, 60000);
        } catch (e) { fallbackDownload((e && e.message) || 'print refused'); }
      }, 150);
    } catch (e) { fallbackDownload((e && e.message) || 'print frame failed'); }
  };

  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState({ objective: '', union: '', carrier: '' });
  const [analysisLoading, setAnalysisLoading] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  // Load collapse state from Firestore
  useEffect(() => {
    if (disciplineCase?.collapseState) {
      setCollapsed(disciplineCase.collapseState);
    }
  }, [disciplineCase?.id]);

  const toggleCollapse = async (boxId) => {
    const newCollapsed = { ...collapsed, [boxId]: !collapsed[boxId] };
    setCollapsed(newCollapsed);
    try {
      await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({
        collapseState: newCollapsed,
        updatedAt: serverTimestamp()
      }, user?.email || auth.currentUser?.email, 'Collapse state toggled'));
    } catch(e) { console.error('Collapse save failed:', e); }
  };

  const CollapseBox = ({ id, title, icon, color, status, children, defaultOpen = true }) => {
    const isOpen = collapsed[id] === undefined ? defaultOpen : !collapsed[id];
    const statusColor = status === 'complete' ? '#40C070' : status === 'generating' ? '#E8A020' : 'var(--steel)';
    const statusIcon = status === 'complete' ? '✓' : status === 'generating' ? '⚙' : '○';
    return (
      <div style={{ marginBottom: 12, border: `1px solid ${color || '#2E4A6B'}`, borderRadius: 8, overflow: 'hidden' }}>
        <div
          {...activate(() => toggleCollapse(id))}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#0D1B2A', cursor: 'pointer', userSelect: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
            <span style={{ fontSize: 12, fontWeight: 700, color: color || '#E8A020' }}>{title}</span>
            <span style={{ fontSize: 10, color: statusColor, fontWeight: 600 }}>{statusIcon} {status === 'complete' ? 'Complete' : status === 'generating' ? 'Generating...' : 'Empty'}</span>
          </div>
          <span style={{ fontSize: 14, color: 'var(--steel)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </div>
        {isOpen && (
          <div style={{ padding: '14px 16px', background: '#0A1628' }}>
            {children}
          </div>
        )}
      </div>
    );
  };

  const userRole = uiRole(user, disciplineCase);  // F-10 (SS193): was user?.role, which is never 'lc' or 'gc'; Record 613 (Bob 10/8): the case decides a tester's
  // Record 592 (functions audit F24): the named member READS the notes and does not write them (ruling 105; the
  // rules' caseParty()). He was offered Add Note and refused, silently. Officers and the case's own author write.
  const mayAddNote = !writesDisciplineOfficerNotes(user, disciplineCase) && (userRole !== 'member' || (!!user?.uid && !!disciplineCase && disciplineCase.createdBy === user.uid));   // Record 637 (305.9): an officer's note goes to the officer card
  const canSeeStrategyDocs = userRole === 'lc' || userRole === 'gc' || userRole === 'admin';

  useEffect(() => {
    if (!caseId) return;
    const unsub = onSnapshot(doc(db, 'disciplineCases', caseId), snap => {
      if (snap.exists()) {
        setDisciplineCase({ id: snap.id, ...snap.data() });
        if (snap.data().hearingLog) setHearingLog(snap.data().hearingLog);
        if (snap.data().lcAssessment) setLcAssessment(snap.data().lcAssessment);
      }
      setLoading(false);
    }, err => {
      // Ruling 66 (Record 582): an administrator refused a direct read of another person's draft reads it
      // through the audited callable (logged to audit_logs). Read-only; the draft's notes stay private.
      if (!shouldTryAuditedRead(user, err)) { setLoading(false); return; }
      adminReadDraft('discipline', caseId)
        .then(d => { setDisciplineCase(d); if (d.hearingLog) setHearingLog(d.hearingLog); if (d.lcAssessment) setLcAssessment(d.lcAssessment); })
        .catch(() => {})
        .finally(() => setLoading(false));
    });
    const unsubNotes = onSnapshot(
      query(collection(db, 'disciplineCases', caseId, 'notes'), orderBy('timestamp', 'asc')),
      snap => setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => setNotes([])
    );
    return () => { unsub(); unsubNotes(); };
  }, [caseId, user]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), {
        text: newNote.trim(),
        authorName: user?.name || user?.email || 'Unknown',
        authorRole: userRole,
        timestamp: serverTimestamp(),
      });
      setNewNote('');
    } catch (e) {   // Record 592 (F24): a refused note is said, and the text stays in the box
      window.alert('The note was NOT saved: ' + ((e && e.message) || 'the write was refused') + '. Your text is still in the box.');
    }
  };

  // Record 611 (F20; Bob 10/8): the ladder's acts are the disciplineAct function's. Open / Advance / Record decision /
  // Resolve / Reopen are DisciplineLadderPanel (the stage bar below); the Forward button further down is the same
  // callable. The browser no longer writes status, caseNumber, a decision or the forward stamps - firestore.rules
  // refuses them. (F-12 holds in may(): a gc_review case does not move up the ladder.)
  const [fwdBusy, setFwdBusy] = useState(false);
  const [fwdErr, setFwdErr] = useState('');
  const forwardToGC = async () => {
    if (!window.confirm('Forward this case to the General Chairperson?\n\nBy forwarding you confirm the investigation record is complete. The case will appear in the General Chairperson\'s discipline inbox on The Committee page. No notice is sent to him. From the forward he holds exclusive authority over this case.')) return;
    setFwdBusy(true); setFwdErr('');
    try {
      // F-5 (SS184.4): nothing reaches the General Chairperson without a case number - the function numbers an
      // unnumbered case in the same transaction as the forward, stamps forwardedToGCAt/By and signs the note.
      await callDisciplineAct({ caseId, kind: 'forward' });
    } catch (e) {
      setFwdErr('The case was NOT forwarded: ' + ((e && e.message) || 'the act was refused') + '. Nothing on the case was changed.');
    } finally { setFwdBusy(false); }
  };

  const uploadDocument = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `discipline/${caseId}/${Date.now()}_${uploadFile.name}`);
      await uploadBytes(storageRef, uploadFile, objTag);
      const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
      const isStrategyDoc = uploadType === 'Carrier Correspondence' || uploadType === 'Appeal Letter' || uploadType === 'Board Award';
      const newDoc = {
        name: uploadFile.name,
        url,
        storagePath,
        type: uploadType,
        uploadedBy: user?.email || '',
        uploadedByName: user?.name || '',
        uploadedByRole: userRole,
        uploadedAt: new Date().toISOString(),
        visibleTo: isStrategyDoc && userRole !== 'member' ? ['lc', 'gc', 'admin'] : ['member', 'lc', 'gc', 'admin'],
        isOriginalNotice: false,
      };
      const existingDocs = disciplineCase.documents || [];
      await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({
        documents: [...existingDocs, newDoc],
        updatedAt: serverTimestamp(),
      }, user?.email || auth.currentUser?.email, `Document uploaded: ${uploadFile.name}`));
      await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), {
        text: `Document uploaded: "${uploadFile.name}" (${uploadType}) by ${user?.name || user?.email}`,
        authorName: user?.name || user?.email,
        authorRole: userRole,
        timestamp: serverTimestamp(),
      });
      setUploadFile(null);
      setUploadType('Other');
    } catch (e) {
      console.error('Upload error:', e);
    } finally {
      setUploading(false);
    }
  };

  const saveHearingLog = async () => {
    setSaving(true);
    await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({
      hearingLog,
      updatedAt: serverTimestamp(),
    }, user?.email || auth.currentUser?.email, 'Hearing log saved'));
    setSaving(false);
    setShowHearingForm(false);
    await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), {
      text: `Investigation hearing log saved. Date: ${hearingLog.hearingDate}. Member plea: ${hearingLog.memberPlea}.`,
      authorName: user?.name || user?.email,
      authorRole: userRole,
      timestamp: serverTimestamp(),
    });
  };

  const saveLcAssessment = async () => {
    setSaving(true);
    await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({
      lcAssessment,
      updatedAt: serverTimestamp(),
    }, user?.email || auth.currentUser?.email, 'LC assessment updated'));
    setSaving(false);
    await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), {
      // CORRECTED 2026-09-22 (section 364, D1). This used to interpolate the strength rating and the
      // recommendation into a note that EVERY PARTY READS, including the member the case is about.
      // Ruling 204: the platform never puts an assessment of a member's position in front of him.
      // The assessment is on the gated lcAssessment field, where it belongs.
      text: 'LC assessment updated.',
      authorName: user?.name || user?.email,
      authorRole: userRole,
      timestamp: serverTimestamp(),
    });
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#8AA4BF' }}>Loading case...</div>;
  if (!disciplineCase) return <div style={{ padding: 40, color: '#FF6B6B' }}>Case not found.</div>;

  const currentStageIdx = CASE_STAGES.findIndex(s => s.key === disciplineCase.status);
  const deadlineDays = daysUntil(disciplineCase.investigationDeadline);
  // F-7 (SS193): this read `|| d.visibleTo?.includes('admin')` - true for every LC/GC-only document,
  // so the member saw all of them. The reader's role, or the reader is admin. Nothing else.
  // F-19 (SS202): the marks a charging letter has carried over time - F-9's key, the creation form's
  // isOriginalNotice, and the type the form has always written. One test, used by the pinned slot and
  // by the unclassified list, so a letter is never both missing and unclassified.
  const isChargingLetter = (d) => !!(d && (d.isOriginalChargeDocument || d.isOriginalNotice || d.type === 'Discipline Notice'));
  const KNOWN_ZONES = ['hearing', 'appeal', 'decision', 'transcript', 'carrier_exhibit'];
  const visibleDocs = (disciplineCase.documents || []).filter(d =>
    !Array.isArray(d.visibleTo) || d.visibleTo.includes(userRole) || userRole === 'admin'   // F-19: no list = open default (pre-visibleTo upload)
  );
  // P-5: every object uploaded from this page carries who and which committee (all-or-none, ruling 74's
  // pattern), so the /discipline/ storage rule can be scoped later without a backfill of these objects.
  // RULINGS 187/190: see disciplineObjectTag() - every upload on this page takes its tag from here or above.
  const objTag = disciplineObjectTag(disciplineCase);

  const tabStyle = (tab) => ({
    padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
    color: activeTab === tab ? '#E8A020' : '#8AA4BF',
    background: 'transparent', border: 'none',
    // ONE declaration only. There were two identical `borderBottom` keys here and esbuild flagged it
    // 2026-08-28. It was harmless - the later won and they matched - but a duplicate key is a defect
    // waiting for someone to edit the first one and see nothing change. This must stay AFTER
    // `border: 'none'`, which would otherwise reset it.
    borderBottom: activeTab === tab ? '2px solid #E8A020' : '2px solid transparent',
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 20px 20px' }}>
      {/* Sticky Navigation Bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: '#060E1A', borderBottom: '1px solid #2E4A6B', padding: '8px 0', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '5px 10px' }} onClick={onBack}>← Back</button>
          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '5px 10px' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>🏠 Top</button>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#E8A020', textAlign: 'center' }}>
          {disciplineCase?.caseNumber || 'Case Detail'}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary" style={{ fontSize: 10, padding: '5px 8px' }}
            title="Collapse all boxes"
            onClick={async () => {
              const keys = ['charging_letter','extracted_record','objections','questions','precedent','exhibits','case_file','witness_request','evidence_request','post_hearing','analysis'];
              const allCollapsed = {};
              keys.forEach(k => allCollapsed[k] = true);
              setCollapsed(allCollapsed);
              try { await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ collapseState: allCollapsed, updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'All sections collapsed')); } catch(e) {}
            }}>▲ Collapse All</button>
          <button className="btn btn-secondary" style={{ fontSize: 10, padding: '5px 8px' }}
            title="Expand all boxes"
            onClick={async () => {
              setCollapsed({});
              try { await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ collapseState: {}, updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'All sections expanded')); } catch(e) {}
            }}>▼ Expand All</button>
          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '5px 10px' }}
            onClick={() => window.print()}>🖨 Print</button>
          {disciplineCase ? <SlidesButton label="▶ Preparation as slides" style={{ fontSize: 11 }} build={() => hearingPrepDeck(disciplineCase)} /> : null}   {/* Ruling 311 with 308 */}
          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '5px 10px' }}
            onClick={async () => {
              try {
                await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'Manual save'));
              } catch(e) { alert('Save failed.'); }
            }}>💾 Save</button>
        </div>
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px', marginBottom: 8 }} onClick={onBack}>
            ← Back to Discipline Cases
          </button>
          {isExampleCase(disciplineCase) && (   /* Record 613 (Bob 10/8), F22: the EXAMPLE ONLY mark (ISS-46 46.7) */
            <div style={{ display: 'inline-block', marginBottom: 6, padding: '3px 10px', border: '2px solid #E8A020', borderRadius: 4, color: '#E8A020', fontWeight: 800, fontSize: 13, letterSpacing: 2 }}>{EXAMPLE_MARK}</div>
          )}
          <div style={{ fontSize: 18, fontWeight: 700, color: '#F4F6F8' }}>{disciplineCase.caseNumber}</div>
          <div style={{ fontSize: 13, color: '#8AA4BF', marginTop: 2 }}>
            {disciplineCase.memberName} · {disciplineCase.generalCommittee} · {disciplineCase.disciplineType}
            {disciplineCase.suspensionDays ? ` (${disciplineCase.suspensionDays} days)` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          {disciplineCase.status !== 'resolved' && deadlineDays !== null && deadlineDays <= 5 && (
            <div style={{ background: '#2D0A0A', border: '1px solid #C0392B', borderRadius: 6, padding: '6px 12px', marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: '#FF6B6B', fontWeight: 700 }}>
                ⚠ Investigation deadline in {deadlineDays} day{deadlineDays !== 1 ? 's' : ''}
              </div>
            </div>
          )}
          <div style={{ fontSize: 11, color: '#8AA4BF' }}>
            Deadline: {formatDate(disciplineCase.investigationDeadline)}
          </div>
        </div>
      </div>

      {/* Lifecycle */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          {CASE_STAGES.map((stage, i) => (
            <React.Fragment key={stage.key}>
              <div style={{
                padding: '6px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: i === currentStageIdx ? '#1A5C3A' : i < currentStageIdx ? '#1A3A5C' : '#0A1628',
                color: i === currentStageIdx ? '#40C070' : i < currentStageIdx ? '#4A9EE8' : 'var(--steel)',
                border: `1px solid ${i === currentStageIdx ? '#40C070' : i < currentStageIdx ? '#2E4A6B' : '#1A2A3A'}`,
              }}>
                {stage.icon} {stage.label}
              </div>
              {i < CASE_STAGES.length - 1 && <div style={{ color: '#2E4A6B', fontSize: 12 }}>→</div>}
            </React.Fragment>
          ))}
        </div>
        {/* Record 611 (F20; Bob 10/8): Open / Advance / Record decision / Resolve / Reopen - each offered where may() allows it */}
        <DisciplineLadderPanel kase={disciplineCase} caseId={caseId} user={user} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #2E4A6B', marginBottom: 16 }}>
        {['overview', 'investigation', 'documents', 'lc_assessment', 'notes'].concat(isLetterOfficer(user) ? ['letters'] : []).map(tab => (
          <button key={tab} style={tabStyle(tab)} onClick={() => setActiveTab(tab)}>
            {tab === 'overview' ? 'Overview' :
             tab === 'investigation' ? '⚖️ Investigation' :
             tab === 'documents' ? '📎 Documents' :
             tab === 'lc_assessment' ? '📋 LC Assessment' :
             tab === 'letters' ? '✉️ Letters' : '📝 Notes'}
          </button>
        ))}
      </div>

      {/* LETTERS TAB - master list 10.16: officers only; the platform produces, the officer sends */}
      {activeTab === 'letters' && isLetterOfficer(user) && (
        <DisciplineLettersPanel kase={disciplineCase} user={user} />
      )}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>Case Overview</div>
          {[
            ['Case Number', disciplineCase.caseNumber],
            ['Charged Employee', disciplineCase.memberName],
            ['Railroad', disciplineCase.railroad],
            ['Committee', disciplineCase.generalCommittee],
            ['Agreement', disciplineCase.applicableAgreement],
            ['Discipline Type', disciplineCase.disciplineType + (disciplineCase.suspensionDays ? ` — ${disciplineCase.suspensionDays} days` : '')],
            ['Held in Abeyance', disciplineCase.heldInAbeyance ? 'Yes' : 'No'],
            ['Date of Notice', formatDate(disciplineCase.noticeDate)],
            ['Investigation Deadline', formatDate(disciplineCase.investigationDeadline) + (deadlineDays !== null ? ` (${deadlineDays} days remaining)` : '')],
            ['Charges', <ChargesView text={disciplineCase.chargesDescription} />],   // F-20
            ['Description', disciplineCase.noticeDescription],
            ['Opened By', disciplineCase.openedBy],
            ['Decision', disciplineCase.decision || 'Pending'],
            ['Decision Date', disciplineCase.decisionDate ? formatDate(disciplineCase.decisionDate) : '—'],
            ['Decision Recorded By', disciplineCase.decidedByName || ''],   // Record 611 (F20; Bob 10/8)
            ...(disciplineCase.decisionTerms ? [['Decision Terms', [   // T5 / D-15
              disciplineCase.decisionTerms.suspensionDays ? disciplineCase.decisionTerms.suspensionDays + ' day(s) suspension' : '',
              disciplineCase.decisionTerms.demerits ? disciplineCase.decisionTerms.demerits + ' demerit(s)' : '',
              disciplineCase.decisionTerms.abeyanceMonths ? disciplineCase.decisionTerms.abeyanceMonths + ' month(s) in abeyance' : '',
              disciplineCase.decisionTerms.effectiveDate ? 'effective ' + disciplineCase.decisionTerms.effectiveDate : '',
            ].filter(Boolean).join(', ') || 'none recorded']] : []),
            ...(canSeeStrategyDocs ? [['Decision Notes', disciplineCase.decisionNotes || '']] : []),
            ['Reopened', disciplineCase.reopenReason ? (disciplineCase.reopenedByName || '') + ': ' + disciplineCase.reopenReason : ''],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 8, padding: '8px 0', borderBottom: '1px solid #1A2A3A' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
              <div style={{ fontSize: 13, color: '#F4F6F8' }}>{value || '—'}</div>
            </div>
          ))}<div style={{ marginTop: 14 }}><HowToPanel kind="discipline" agreement={disciplineCase.applicableAgreement} audience={mayOpen(user, 'new-case') ? 'officer' : 'member'} /></div>
          <DisciplineAppealPanel disciplineCase={disciplineCase} user={user} />   {/* T5 / D-19: the appeal ladder */}
          <DisciplineBoardPanel disciplineCase={disciplineCase} user={user} />   {/* T5 / D-20: the path to arbitration */}
          <DisciplineWrittenReportPanel disciplineCase={disciplineCase} user={user} />   {/* T5 / D-23: the written decision, Sec. 44 */}
          <DisciplineMemberActsPanel disciplineCase={disciplineCase} user={user} />   {/* T5 / D-9: the member's own voice (rulings 139, 176) */}
        </div>
      )}

      {/* INVESTIGATION TAB */}
      {activeTab === 'investigation' && (
        <div>
          <HearingActs disciplineCase={disciplineCase} caseId={caseId} user={user} userRole={userRole} canAct={canSeeStrategyDocs} />
          <HearingRecordPanel disciplineCase={disciplineCase} caseId={caseId} user={user} canAct={canSeeStrategyDocs} />   {/* T5 / D-12 */}
          <DisciplineProvisionsPanel disciplineCase={disciplineCase} />   {/* T5 / D-3, D-13: the agreement's words, by phase */}
          {disciplineCase.hearingLog ? (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div className="card-title">Investigation Hearing Log</div>
                {canSeeStrategyDocs && (
                  <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setShowHearingForm(true)}>Edit</button>
                )}
              </div>
              {[
                ['Date', formatDate(disciplineCase.hearingLog.hearingDate)],
                ['Time', disciplineCase.hearingLog.hearingTime],
                ['Location', disciplineCase.hearingLog.location],
                ['Hearing Officer', disciplineCase.hearingLog.hearingOfficer],
                ['Carrier Officers Present', disciplineCase.hearingLog.carriersPresent],
                ['Union Representatives Present', disciplineCase.hearingLog.unionRepsPresent],
                ['Member Present', disciplineCase.hearingLog.memberPresent ? 'Yes' : 'No'],
                ['Member Plea', disciplineCase.hearingLog.memberPlea],
                ['Postponements', disciplineCase.hearingLog.postponements],
                ['Witnesses', disciplineCase.hearingLog.witnesses],
                ['Notes', disciplineCase.hearingLog.transcriptNotes],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, padding: '7px 0', borderBottom: '1px solid #1A2A3A' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#F4F6F8' }}>{value || '—'}</div>
                </div>
              ))}
            </div>
          ) : canSeeStrategyDocs ? (
            <div className="card" style={{ textAlign: 'center', padding: 32 }}>
              <div style={{ fontSize: 14, color: '#8AA4BF', marginBottom: 16 }}>No investigation hearing logged yet.</div>
              <button className="btn btn-primary" onClick={() => setShowHearingForm(true)}>⚖️ Log Investigation Hearing</button>
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: 32, color: '#8AA4BF' }}>
              Investigation hearing details will appear here once logged by the Local Chairperson.
            </div>
          )}

          {showHearingForm && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-title" style={{ marginBottom: 16 }}>Log Investigation Hearing</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date of Hearing</label>
                  <input aria-label="Date of Hearing" className="form-input" type="date" value={hearingLog.hearingDate} onChange={e => setHearingLog(p => ({ ...p, hearingDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Time</label>
                  <input aria-label="Time" className="form-input" type="time" value={hearingLog.hearingTime} onChange={e => setHearingLog(p => ({ ...p, hearingTime: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input aria-label="Location" className="form-input" value={hearingLog.location} onChange={e => setHearingLog(p => ({ ...p, location: e.target.value }))} placeholder="Yard office, division HQ, virtual, etc." />
              </div>
              <div className="form-group">
                <label className="form-label">Hearing Officer (Name & Title)</label>
                <input aria-label="Hearing Officer (Name & Title)" className="form-input" value={hearingLog.hearingOfficer} onChange={e => setHearingLog(p => ({ ...p, hearingOfficer: e.target.value }))} placeholder="e.g. J. Smith, Division Manager" />
              </div>
              <div className="form-group">
                <label className="form-label">Carrier Officers Present (Names & Titles)</label>
                <textarea aria-label="Carrier Officers Present (Names & Titles)" className="form-textarea" rows={2} value={hearingLog.carriersPresent} onChange={e => setHearingLog(p => ({ ...p, carriersPresent: e.target.value }))} placeholder="e.g. D. Kingsland, Trainmaster; R. Behrens, Road Foreman" />
              </div>
              <div className="form-group">
                <label className="form-label">Union Representatives Present (Names & Roles)</label>
                <textarea aria-label="Union Representatives Present (Names & Roles)" className="form-textarea" rows={2} value={hearingLog.unionRepsPresent} onChange={e => setHearingLog(p => ({ ...p, unionRepsPresent: e.target.value }))} placeholder="e.g. Bob Smith, Local Chairperson; Jane Doe, General Chairperson" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <input aria-label="Member Present at Hearing" type="checkbox" checked={hearingLog.memberPresent} onChange={e => setHearingLog(p => ({ ...p, memberPresent: e.target.checked }))} style={{ marginRight: 8, accentColor: '#E8A020' }} />
                    Member Present at Hearing
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label">Member Plea</label>
                  <select aria-label="Member Plea" className="form-select" value={hearingLog.memberPlea} onChange={e => setHearingLog(p => ({ ...p, memberPlea: e.target.value }))}>
                    <option value="Not Guilty">Not Guilty</option>
                    <option value="Guilty">Guilty</option>
                    <option value="No Contest">No Contest</option>
                    <option value="Declined to Plead">Declined to Plead</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Number of Postponements</label>
                <input aria-label="Number of Postponements" className="form-input" type="number" min="0" value={hearingLog.postponements} onChange={e => setHearingLog(p => ({ ...p, postponements: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Witnesses (Names — note if called or denied)</label>
                <textarea aria-label="Witnesses (Names — note if called or denied)" className="form-textarea" rows={3} value={hearingLog.witnesses} onChange={e => setHearingLog(p => ({ ...p, witnesses: e.target.value }))} placeholder="e.g. Pete Jones (called) — Yardmaster; Dave Smith (denied by carrier) — Engineer" />
              </div>
              <div className="form-group">
                <label className="form-label">Hearing Notes / Summary</label>
                <textarea aria-label="Hearing Notes / Summary" className="form-textarea" rows={4} value={hearingLog.transcriptNotes} onChange={e => setHearingLog(p => ({ ...p, transcriptNotes: e.target.value }))} placeholder="Summary of what was said, carrier stated charges, union's defense..." />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setShowHearingForm(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveHearingLog} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Hearing Log'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DOCUMENTS TAB */}
      {activeTab === 'documents' && (
        <div>
          {/* PINNED — Original Charging Letter */}
          {(() => {
            // F-19 (SS202): letters uploaded before F-9 carry isOriginalNotice or type 'Discipline Notice', not the F-9 key.
            const chargingLetter = (disciplineCase.documents || []).find(isChargingLetter);
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#E8A020', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
                  ★ Original Charging Letter
                </div>
                {chargingLetter ? (
                  <div style={{ background: '#1A1400', border: '2px solid #C9A84C', borderRadius: 8, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#E8A020', fontWeight: 700 }}>📋 {chargingLetter.name}</div>
                      <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 3 }}>
                        Uploaded by {chargingLetter.uploadedByName || chargingLetter.uploadedBy} ({chargingLetter.uploadedByRole}) · {new Date(chargingLetter.uploadedAt).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: 11, color: '#C9A84C', marginTop: 2 }}>★ Original Charging Document — Pinned</div>
                    </div>
                    <a href={chargingLetter.url || '#'} onClick={viewStoredFile(storage, chargingLetter.url, chargingLetter.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '6px 14px', borderColor: '#C9A84C', color: '#E8A020' }}>View</a>
                  </div>
                ) : (
                  <div style={{ background: '#1A1400', border: '2px dashed #C9A84C', borderRadius: 8, padding: '16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: '#8AA4BF', marginBottom: 10 }}>No charging letter uploaded yet.</div>
                    <div style={{ fontSize: 11, color: 'var(--steel)', marginBottom: 12 }}>
                      Upload the original charging letter here. The platform will read it and extract the charge language automatically.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" id="charge-letter-upload"
                        onChange={async e => {
                          const file = e.target.files[0];
                          if (!file) return;
                          setUploading(true);
                          try {
                                    const storageRef = ref(storage, `discipline/${caseId}/charging_letter_${Date.now()}_${file.name}`);
                            await uploadBytes(storageRef, file, objTag);
                            const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
                            const newDoc = {
                              name: file.name, url, storagePath,
                              type: 'Discipline Notice',
                              uploadedBy: user?.email || '',
                              uploadedByName: user?.name || '',
                              uploadedByRole: userRole,
                              uploadedAt: new Date().toISOString(),
                              visibleTo: ['member', 'lc', 'gc', 'admin'],
                              isOriginalNotice: true,
                              isOriginalChargeDocument: true,
                            };
                            const existingDocs = disciplineCase.documents || [];
                            await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({
                              documents: [newDoc, ...existingDocs],
                              updatedAt: serverTimestamp(),
                            }, user?.email || auth.currentUser?.email, `Charging letter uploaded: ${file.name}`));
                            await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), {
                              text: `Original Charging Letter uploaded: "${file.name}" by ${user?.name || user?.email}`,
                              authorName: user?.name || user?.email,
                              authorRole: userRole,
                              timestamp: serverTimestamp(),
                            });
                          } catch(err) {
                            console.error('Upload error:', err);
                          } finally {
                            setUploading(false);
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="charge-letter-upload" className="btn btn-primary" style={{ cursor: 'pointer', borderColor: '#C9A84C', background: '#1A1400', color: '#E8A020', padding: '8px 20px' }}>
                        {uploading ? '⚙ Uploading...' : '📋 Upload Original Charging Letter'}
                      </label>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ZONE 2 — Hearing Documents */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#4A9EE8', textTransform: 'uppercase', letterSpacing: 2 }}>⚖️ Hearing Documents</div>
                <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2 }}>Investigation transcript, postponement & cancellation notices, pre-hearing correspondence</div>
              </div>
            </div>
            {(() => {
              const hearingDocs = visibleDocs.filter(d => !isChargingLetter(d) && d.zone === 'hearing');
              return hearingDocs.length === 0 ? (
                <div style={{ color: 'var(--steel)', fontSize: 12, padding: '8px 0', fontStyle: 'normal' }}>No hearing documents yet.</div>
              ) : hearingDocs.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #1A2A3A' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 2 }}>
                      {d.type} · {d.uploadedByName || d.uploadedBy} · {new Date(d.uploadedAt).toLocaleDateString()}
                      {!d.visibleTo?.includes('member') && <span style={{ color: '#FF6B6B', marginLeft: 8 }}>🔒 LC/GC Only</span>}
                    </div>
                  </div>
                  <a href={d.url || '#'} onClick={viewStoredFile(storage, d.url, d.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>View</a>
                </div>
              ));
            })()}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1A2A3A' }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>Document Type</label>
                  <select aria-label="Document Type" className="form-select" value={hearingUploadType} onChange={e => setHearingUploadType(e.target.value)}>
                    {HEARING_DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>Select File</label>
                  <input aria-label="Select File" type="file" accept=".pdf,.doc,.docx,.jpg,.png,.xlsx" onChange={e => setHearingUploadFile(e.target.files[0])} style={{ color: '#F4F6F8' }} />
                </div>
              </div>
              <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: '#4A9EE8', color: '#4A9EE8' }}
                disabled={!hearingUploadFile || uploading}
                onClick={async () => {
                  if (!hearingUploadFile) return;
                  setUploading(true);
                  try {
                    const storageRef = ref(storage, `discipline/${caseId}/hearing_${Date.now()}_${hearingUploadFile.name}`);
                    await uploadBytes(storageRef, hearingUploadFile, objTag);
                    const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
                    const isLcGcOnly = userRole !== 'member';
                    const newDoc = { name: hearingUploadFile.name, url, storagePath, type: hearingUploadType, uploadedBy: user?.email || '', uploadedByName: user?.name || '', uploadedByRole: userRole, uploadedAt: new Date().toISOString(), visibleTo: isLcGcOnly ? ['lc','gc','admin'] : ['member','lc','gc','admin'], zone: 'hearing', isOriginalChargeDocument: false };
                    await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ documents: [...(disciplineCase.documents || []), newDoc], updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, `Hearing document uploaded: ${hearingUploadFile.name}`));
                    await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), { text: `Hearing document uploaded: "${hearingUploadFile.name}" (${hearingUploadType})`, authorName: user?.name || user?.email, authorRole: userRole, timestamp: serverTimestamp() });
                    setHearingUploadFile(null);
                  } catch(e) { console.error(e); } finally { setUploading(false); }
                }}>
                {uploading ? 'Uploading...' : '⚖️ Upload Hearing Document'}
              </button>
            </div>
          </div>

          {/* ZONE 3 — Appeal Preparation */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#40C070', textTransform: 'uppercase', letterSpacing: 2 }}>📋 Appeal Preparation</div>
                <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2 }}>Post-hearing exhibits, board awards, precedent cases, appeal letters, all evidence supporting the appeal</div>
              </div>
            </div>
            {(() => {
              const appealDocs = visibleDocs.filter(d => !isChargingLetter(d) && d.zone === 'appeal');
              return appealDocs.length === 0 ? (
                <div style={{ color: 'var(--steel)', fontSize: 12, padding: '8px 0', fontStyle: 'normal' }}>No appeal documents yet.</div>
              ) : appealDocs.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #1A2A3A' }}>
                  <div>
                    <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 2 }}>
                      {d.type} · {d.uploadedByName || d.uploadedBy} · {new Date(d.uploadedAt).toLocaleDateString()}
                      {!d.visibleTo?.includes('member') && <span style={{ color: '#FF6B6B', marginLeft: 8 }}>🔒 LC/GC Only</span>}
                    </div>
                  </div>
                  <a href={d.url || '#'} onClick={viewStoredFile(storage, d.url, d.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>View</a>
                </div>
              ));
            })()}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1A2A3A' }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>Document Type</label>
                  <select aria-label="Document Type" className="form-select" value={appealUploadType} onChange={e => setAppealUploadType(e.target.value)}>
                    {APPEAL_DOCUMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>Select File</label>
                  <input aria-label="Select File" type="file" accept=".pdf,.doc,.docx,.jpg,.png,.xlsx" onChange={e => setAppealUploadFile(e.target.files[0])} style={{ color: '#F4F6F8' }} />
                </div>
              </div>
              <button className="btn btn-secondary" style={{ fontSize: 11, borderColor: '#40C070', color: '#40C070' }}
                disabled={!appealUploadFile || uploading}
                onClick={async () => {
                  if (!appealUploadFile) return;
                  setUploading(true);
                  try {
                    const storageRef = ref(storage, `discipline/${caseId}/appeal_${Date.now()}_${appealUploadFile.name}`);
                    await uploadBytes(storageRef, appealUploadFile, objTag);
                    const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
                    const isLcGcOnly = ['Appeal Letter','Board Award / PLB Award','Arbitration Award','Precedent Case','Union Submission — Appeal'].includes(appealUploadType) && userRole !== 'member';
                    const newDoc = { name: appealUploadFile.name, url, storagePath, type: appealUploadType, uploadedBy: user?.email || '', uploadedByName: user?.name || '', uploadedByRole: userRole, uploadedAt: new Date().toISOString(), visibleTo: isLcGcOnly ? ['lc','gc','admin'] : ['member','lc','gc','admin'], zone: 'appeal', isOriginalChargeDocument: false };
                    await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ documents: [...(disciplineCase.documents || []), newDoc], updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, `Appeal document uploaded: ${appealUploadFile.name}`));
                    await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), { text: `Appeal document uploaded: "${appealUploadFile.name}" (${appealUploadType})`, authorName: user?.name || user?.email, authorRole: userRole, timestamp: serverTimestamp() });
                    setAppealUploadFile(null);
                  } catch(e) { console.error(e); } finally { setUploading(false); }
                }}>
                {uploading ? 'Uploading...' : '📋 Upload Appeal Document'}
              </button>
            </div>
          </div>
          {/* F-19 (SS202): a document with no zone and no charging-letter mark predates the keys the lists
              above use. It is on the record; it is shown here rather than nowhere. */}
          {(() => {
            const other = visibleDocs.filter(d => !isChargingLetter(d) && !KNOWN_ZONES.includes(d.zone));
            if (!other.length) return null;
            return (
              <div style={{ background: '#0D1B2A', border: '1px solid var(--steel)', borderRadius: 8, padding: 14, marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 2 }}>📁 On file, unclassified</div>
                <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2, marginBottom: 6 }}>Uploaded before the document zones existed. On the record; not assigned to a zone above.</div>
                {other.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #1A2A3A' }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{d.name}</div>
                      <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 2 }}>{d.type || 'untyped'} · {d.uploadedByName || d.uploadedBy || 'uploader not recorded'} · {d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString() : 'date not recorded'}</div>
                    </div>
                    <a href={d.url || '#'} onClick={viewStoredFile(storage, d.url, d.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>View</a>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}


      {/* POST-HEARING — CARRIER SUBMISSIONS */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4, borderBottom: '2px solid #E8A020', paddingBottom: 6 }}>
          📬 Post-Hearing — Carrier Submissions
        </div>
        <div style={{ fontSize: 11, color: 'var(--steel)', marginBottom: 14 }}>
          Upload all carrier materials received after the investigation. These become part of the permanent appeal record.
        </div>

        {/* Zone 4 — Decision Letters */}
        <div style={{ background: '#0D1B2A', border: '1px solid #C0392B', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#FF6B6B', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
            📋 Letters of Decision & Assessment of Discipline
          </div>
          {(() => {
            const decisionDocs = (disciplineCase.documents || []).filter(d => d.zone === 'decision');
            return decisionDocs.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--steel)', fontStyle: 'italic', marginBottom: 10 }}>No decision letters uploaded yet.</div>
            ) : decisionDocs.map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1A2A3A' }}>
                <div>
                  <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>{d.type} · {d.uploadedByName} · {new Date(d.uploadedAt).toLocaleDateString()}</div>
                </div>
                <a href={d.url || '#'} onClick={viewStoredFile(storage, d.url, d.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>View</a>
              </div>
            ));
          })()}
          <div style={{ marginTop: 10 }}>
            <input type="file" accept=".pdf,.doc,.docx" id="decision-upload"
              onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                setUploading(true);
                try {
                  const storageRef = ref(storage, `discipline/${caseId}/decision_${Date.now()}_${file.name}`);
                  await uploadBytes(storageRef, file, objTag);
                  const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
                  const newDoc = { name: file.name, url, storagePath, type: 'Letter of Decision', uploadedBy: user?.email || '', uploadedByName: user?.name || '', uploadedByRole: userRole, uploadedAt: new Date().toISOString(), visibleTo: ['lc','gc','admin'], zone: 'decision', isOriginalChargeDocument: false };
                  await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ documents: [...(disciplineCase.documents || []), newDoc], updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, `Decision letter uploaded: ${file.name}`));
                  await addDoc(collection(db, 'disciplineCases', caseId, 'notes'), { text: `Decision letter uploaded: "${file.name}"`, authorName: user?.name || user?.email, authorRole: userRole, timestamp: serverTimestamp() });
                } catch(e) { console.error(e); } finally { setUploading(false); }
              }}
              style={{ display: 'none' }} />
            <label htmlFor="decision-upload" className="btn btn-secondary" style={{ cursor: 'pointer', fontSize: 11, color: '#FF6B6B', borderColor: '#C0392B' }}>
              {uploading ? 'Uploading...' : '📋 Upload Decision Letter'}
            </label>
          </div>
        </div>

        {/* Zone 5 — Transcript & Recordings */}
        <div style={{ background: '#0D1B2A', border: '1px solid #4A9EE8', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#4A9EE8', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
            🎙 Investigation Transcript & Recordings
          </div>
          {(() => {
            const transcriptDocs = (disciplineCase.documents || []).filter(d => d.zone === 'transcript');
            return transcriptDocs.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--steel)', fontStyle: 'italic', marginBottom: 10 }}>No transcript or recordings uploaded yet.</div>
            ) : transcriptDocs.map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1A2A3A' }}>
                <div>
                  <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>{d.type} · {d.uploadedByName} · {new Date(d.uploadedAt).toLocaleDateString()}</div>
                </div>
                <a href={d.url || '#'} onClick={viewStoredFile(storage, d.url, d.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>View</a>
              </div>
            ));
          })()}
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            {['Transcript', 'Audio Recording', 'Video Recording'].map(type => (
              <div key={type} style={{ position: 'relative' }}>
                <input aria-label="Attach the transcript or recording" type="file" accept=".pdf,.doc,.docx,.mp3,.mp4,.wav,.mov" id={`transcript-${type}`}
                  onChange={async e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      // Record 613 (Bob 10/8), ruling 157: a RECORDING is stored only when the carrier produced it - asked here,
                      // then tagged mediaOrigin carrier_produced (storage.rules recordingAllowed()); otherwise it is not uploaded
                      // and he is pointed to the Recordings tracker below. A transcript (a document) passes through unchanged.
                      const rec613 = recordingUploadTag(file, objTag, (q) => window.confirm(q));
                      if (rec613.refused) { window.alert(rec613.refused); e.target.value = ''; return; }
                      const storageRef = ref(storage, `discipline/${caseId}/transcript_${Date.now()}_${file.name}`);
                      await uploadBytes(storageRef, file, rec613.tag);
                      const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
                      const newDoc = { name: file.name, url, storagePath, type, uploadedBy: user?.email || '', uploadedByName: user?.name || '', uploadedByRole: userRole, uploadedAt: new Date().toISOString(), visibleTo: ['lc','gc','admin'], zone: 'transcript', isOriginalChargeDocument: false };
                      await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ documents: [...(disciplineCase.documents || []), newDoc], updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, `Transcript uploaded: ${file.name}`));
                    } catch(e) { console.error(e); window.alert('NOT uploaded: ' + ((e && (e.code || e.message)) || 'the upload was refused') + '.'); } finally { setUploading(false); }   // Record 613: a refused upload is said
                  }}
                  style={{ display: 'none' }} />
                <label htmlFor={`transcript-${type}`} className="btn btn-secondary" style={{ cursor: 'pointer', fontSize: 10, padding: '4px 8px', color: '#4A9EE8', borderColor: '#4A9EE8' }}>
                  + {type}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Record 613 (Bob 10/8), ruling 157: the recordings tracker - every recording on the case, whoever made it. */}
        <MediaTracker parentKind="discipline" parentId={caseId} parent={disciplineCase} documents={disciplineCase.documents || []} user={user} />

        {/* Zone 6 — Carrier Exhibits */}
        <div style={{ background: '#0D1B2A', border: '1px solid #8A4AE8', borderRadius: 8, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#8A4AE8', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>
            📁 Carrier Exhibits
          </div>
          <div style={{ fontSize: 11, color: 'var(--steel)', marginBottom: 8 }}>All exhibits submitted by the carrier — before and at the hearing.</div>
          {(() => {
            const carrierDocs = (disciplineCase.documents || []).filter(d => d.zone === 'carrier_exhibit');
            return carrierDocs.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--steel)', fontStyle: 'italic', marginBottom: 10 }}>No carrier exhibits uploaded yet.</div>
            ) : carrierDocs.map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1A2A3A' }}>
                <div>
                  <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>{d.type} · {d.uploadedByName} · {new Date(d.uploadedAt).toLocaleDateString()}</div>
                </div>
                <a href={d.url || '#'} onClick={viewStoredFile(storage, d.url, d.storagePath)} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}>View</a>
              </div>
            ));
          })()}
          <div style={{ marginTop: 10 }}>
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.png,.xlsx" id="carrier-exhibit-upload"
              onChange={async e => {
                const file = e.target.files[0];
                if (!file) return;
                setUploading(true);
                try {
                  const storageRef = ref(storage, `discipline/${caseId}/carrier_${Date.now()}_${file.name}`);
                  await uploadBytes(storageRef, file, objTag);
                  const url = '', storagePath = storageRef.fullPath;   // Record 591 (ruling 247): no link at upload - the file is unreadable until scanned; View resolves the path
                  const newDoc = { name: file.name, url, storagePath, type: 'Carrier Exhibit', uploadedBy: user?.email || '', uploadedByName: user?.name || '', uploadedByRole: userRole, uploadedAt: new Date().toISOString(), visibleTo: ['lc','gc','admin'], zone: 'carrier_exhibit', isOriginalChargeDocument: false };
                  await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ documents: [...(disciplineCase.documents || []), newDoc], updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, `Carrier exhibit uploaded: ${file.name}`));
                } catch(e) { console.error(e); } finally { setUploading(false); }
              }}
              style={{ display: 'none' }} />
            <label htmlFor="carrier-exhibit-upload" className="btn btn-secondary" style={{ cursor: 'pointer', fontSize: 11, color: '#8A4AE8', borderColor: '#8A4AE8' }}>
              📁 Upload Carrier Exhibit
            </label>
          </div>
        </div>
      </div>

      {/* ANALYSIS - Record 609 (ruling 293): one box remains; the two argument boxes are removed */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4, borderBottom: '2px solid #E8A020', paddingBottom: 6 }}>
          ⚖️ Case Analysis & Appeal Preparation
        </div>
        <div style={{ fontSize: 11, color: 'var(--steel)', marginBottom: 14 }}>
          Generate after all carrier materials are uploaded. These analyses form the foundation of the appeal.
        </div>

        {/* Box 1 — Objective Analysis */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#8AA4BF' }}>📊 Objective Case Analysis</div>
              <div style={{ fontSize: 11, color: 'var(--steel)', marginTop: 2 }}>The record on both sides - what each side has put on the record, what it does not contain, and the dated procedural acts. No rating, no prediction, no recommendation. [R-293]</div>
            </div>
            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 12px', whiteSpace: 'nowrap' }}
              disabled={analysisLoading === 'objective'}
              onClick={async () => {
                setAnalysisLoading('objective');
                setAnalysis(prev => ({ ...prev, objective: '⚙ Analyzing both cases...' }));
                try {
                  const desc = `UNION CASE RECORD:\n${disciplineCase.chargesDescription || ''}\n\nPROCEDURAL OBJECTIONS:\n${disciplineCase.proceduralObjections || ''}\n\nINVESTIGATION QUESTIONS:\n${disciplineCase.investigationQuestions || ''}\n\nLC ASSESSMENT:\n${disciplineCase.lcAssessment?.stepNotes || ''}`;
                  const r = await fetch(ANALYZE_URL, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
                    body: JSON.stringify({ mode: 'discipline_objective_analysis', agreement: disciplineCase.applicableAgreement || '', description: desc })
                  });
                  const data = await r.json();
                  const result = data.result || 'Could not generate analysis.';
                  setAnalysis(prev => ({ ...prev, objective: result }));
                  await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ 'analysis.objective': result, updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'Objective analysis generated'));
                } catch(e) { setAnalysis(prev => ({ ...prev, objective: 'Error generating analysis.' })); }
                finally { setAnalysisLoading(null); }
              }}>
              {analysisLoading === 'objective' ? '⚙ Analyzing...' : '📊 Generate Analysis'}
            </button>
          </div>
          {(analysis.objective || disciplineCase.analysis?.objective) && <DraftReasoningBanner text={analysis.objective || disciplineCase.analysis?.objective} />}
          <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '12px 14px', fontSize: 12, color: '#C8D8E8', lineHeight: 1.8, whiteSpace: 'pre-wrap', minHeight: 80 }}>
            {plainMd(analysis.objective || disciplineCase.analysis?.objective) || <span style={{ color: 'var(--steel)', fontStyle: 'italic' }}>Generate after uploading all carrier materials above.</span>}
          </div>
        </div>

        {/* Record 609 (ruling 293, Bob 2026-10-08): the union appellate-position box and the carrier anticipated-position
            box are removed - a composed argument, for either side, is the officer's to write. Text already stored on a
            case (analysis.union, analysis.carrier) stays in the record; it is no longer shown or generated. */}

        {/* LC → GC ESCALATION GATE */}
        {/* F-18 (SS202): Forward is the Local Chairperson's handoff - it notifies the GC, stamps the record and
            moves the case to gc_review. It was offered to the General Chairperson himself. A GC holding a
            vacant seat's work (ruling 60) uses Advance; he does not forward a case to himself. */}
        {ladderMay(ladderCaller(user), 'forward', disciplineCase).ok && (
          <div style={{ background: '#1A0A0A', border: '2px solid #C0392B', borderRadius: 10, padding: '20px 24px', marginTop: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#FF6B6B', marginBottom: 8 }}>
              📤 Forward Case to General Chairperson
            </div>
            <div style={{ fontSize: 12, color: '#F4A0A0', lineHeight: 1.7, marginBottom: 16 }}>
              When the investigation record is complete — charging letter, objections, questions, transcript, carrier exhibits, and analysis — forward the case to the General Chairperson. The GC will assume exclusive authority over this case from this point forward.
            </div>
            <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 16, background: '#0D1B2A', padding: '10px 14px', borderRadius: 6 }}>
              <strong style={{ color: '#E8A020' }}>What happens when you forward:</strong><br />
              1. The case appears in the GC's discipline inbox on The Committee page. No notice is sent.<br />
              2. A permanent timestamp is recorded in the case record<br />
              3. The case status changes to GC Review<br />
              4. The GC assumes exclusive authority over next steps
            </div>
            <button className="btn btn-primary" style={{ background: '#C0392B', borderColor: '#C0392B', fontSize: 13, padding: '10px 24px' }}
              disabled={fwdBusy} onClick={forwardToGC}>
              📤 Forward to General Chairperson — Case Complete
            </button>
            {fwdErr ? <div style={{ fontSize: 12, color: '#FF6B6B', marginTop: 10 }}>&#9888; {fwdErr}</div> : null}
          </div>
        )}

        {disciplineCase.status === 'gc_review' && (
          <div style={{ background: '#0A1628', border: '2px solid #E8A020', borderRadius: 10, padding: '16px 20px', marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8A020', marginBottom: 4 }}>
              ⚖️ Case in GC Review
            </div>
            <div style={{ fontSize: 12, color: '#8AA4BF' }}>
              This case was forwarded to the General Chairperson on {disciplineCase.forwardedToGCAt?.toDate?.()?.toLocaleDateString() || 'record date'} by {disciplineCase.forwardedToGCBy}. The General Chairperson has exclusive authority over next steps: he records the decision, and resolves or reopens the case, from the stage bar at the top of this page.
            </div>
          </div>
        )}
      </div>

      {/* LC ASSESSMENT TAB */}
      {activeTab === 'lc_assessment' && (
        canSeeStrategyDocs ? (
          <div>
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div className="card-title" style={{ marginBottom: 2 }}>📋 LC / GC Assessment</div>
                  <div style={{ fontSize: 11, color: '#8AA4BF' }}>AI analysis of the full case record. Edit as needed. LC and GC only.</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }} disabled={analysisLoading === 'lc_assessment'}
                    onClick={async () => {
                      setAnalysisLoading('lc_assessment');
                      const desc = 'CASE: ' + disciplineCase.caseNumber + '\nMEMBER: ' + disciplineCase.memberName + '\nAGREEMENT: ' + disciplineCase.applicableAgreement + '\nCHARGES: ' + (disciplineCase.chargesDescription || '') + '\nLC NOTES: ' + (lcAssessment.stepNotes || 'None');
                      try {
                        const r = await fetch(ANALYZE_URL, {
                          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
                          body: JSON.stringify({ mode: 'discipline_lc_assessment', agreement: disciplineCase.applicableAgreement || '', description: desc })
                        });
                        const data = await r.json();
                        const result = data.result || '';
                        setLcAssessment(p => ({ ...p, aiAssessment: result }));
                        await updateDoc(doc(db, 'disciplineCases', caseId), logAudit({ lcAssessment: { ...lcAssessment, aiAssessment: result }, updatedAt: serverTimestamp() }, user?.email || auth.currentUser?.email, 'AI assessment generated'));
                      } catch(e) { console.error(e); } finally { setAnalysisLoading(null); }
                    }}>
                    {analysisLoading === 'lc_assessment' ? '⚙ Analyzing...' : '⚙ Generate AI Assessment'}
                  </button>
                  <button className="btn btn-secondary" style={{ fontSize: 11, padding: '4px 10px' }}
                    onClick={() => exportBox('LC Assessment', lcAssessment.aiAssessment || lcAssessment.stepNotes || 'No assessment yet.')}>
                    📄 Export
                  </button>
                </div>
              </div>
              {lcAssessment.aiAssessment ? (
                <div><DraftReasoningBanner text={lcAssessment.aiAssessment} /><div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '12px 14px', fontSize: 12, color: '#C8D8E8', lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 14 }}>{plainMd(lcAssessment.aiAssessment)}</div></div>
              ) : (
                <div style={{ background: '#0A1628', border: '1px dashed #2E4A6B', borderRadius: 6, padding: 20, textAlign: 'center', marginBottom: 14, color: 'var(--steel)', fontSize: 12 }}>
                  No AI assessment generated yet. Click Generate AI Assessment above.
                </div>
              )}
              <div className="form-group"><label className="form-label">Step Notes (visible to member)</label>
                <textarea aria-label="Step Notes (visible to member)" className="form-textarea" rows={3} value={lcAssessment.stepNotes || ''} onChange={e => setLcAssessment(p => ({ ...p, stepNotes: e.target.value }))} placeholder="What happened at each step..." /></div>
              <div className="form-group"><label className="form-label">Strategy Notes (LC/GC only)</label>
                <textarea aria-label="Strategy Notes (LC/GC only)" className="form-textarea" rows={3} value={lcAssessment.strategyNotes || ''} onChange={e => setLcAssessment(p => ({ ...p, strategyNotes: e.target.value }))} placeholder="Legal strategy, weaknesses, settlement considerations..." /></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 10, color: 'var(--steel)' }}>🔒 LC and GC only.</div>
                <button className="btn btn-primary" onClick={saveLcAssessment} disabled={saving}>{saving ? 'Saving...' : '💾 Save Notes'}</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: '#8AA4BF' }}>LC Assessment is visible to LC and GC only.</div>
        )
      )}

      {/* NOTES TAB */}{activeTab === 'notes' && <DisciplineOfficerNotes caseId={caseId} disciplineCase={disciplineCase} user={user} />}
      {activeTab === 'notes' && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>Case Notes & Communications Log</div>
          <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 16 }}>
            Permanent record — no deletions permitted. Everyone on the case, the member it names included, reads this log; an officer writes his own notes in the card above.
          </div>
          {notes.length === 0 ? (
            <div style={{ color: 'var(--steel)', fontSize: 13, padding: '16px 0' }}>No notes yet.</div>
          ) : notes.map(note => (
            <div key={note.id} style={{ padding: '12px 0', borderBottom: '1px solid #1A2A3A' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: note.isSystemNote ? '#E8A020' : '#8AA4BF', textTransform: 'uppercase' }}>
                  {note.isSystemNote ? '⚙ System' : `${note.authorName} (${note.authorRole})`}
                </div>
                <div style={{ fontSize: 11, color: 'var(--steel)' }}>
                  {note.timestamp?.toDate?.()?.toLocaleString() || ''}
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.6 }}>{note.text}</div>
            </div>
          ))}
          {mayAddNote && (   /* Record 592 (F24) */
          <div style={{ marginTop: 16 }}>
            <textarea aria-label="Add a case note — document events, decisions, communications, or directives..." className="form-textarea" rows={3} value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a case note — document events, decisions, communications, or directives..." />
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={addNote} disabled={!newNote.trim()}>
                Add Note
              </button>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DISCIPLINE CASES LIST ───────────────────────────────────────────────────
export default function DisciplineCases({ user, initialView, initialCaseId }) {   // initialCaseId: 6a-06 (section 250) - opened from a member's file
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(initialView === 'new' ? 'new' : initialCaseId ? 'detail' : 'list'); // list | new | detail  (ruling 129: the sidebar's New Case door opens here; 6a-06: a member's file opens a case)
  const [selectedCaseId, setSelectedCaseId] = useState(initialCaseId || null);
  const [filter, setFilter] = useState('All');

  const [loadError, setLoadError] = useState('');
  useEffect(() => {
    // F-11 (SS193): this was one whole-collection query, which the read rule lets only admin run; a
    // GC, LC or member was denied and left on "Loading cases..." with no error path. The query now
    // states the reader's own scope, which is what the rule can prove: admin - everything; a General
    // Chairperson - his committee; anyone else - the cases he created. No orderBy on the scoped
    // queries (a where + orderBy on different fields needs a composite index, which is a -Rules
    // deploy); they are sorted here instead.
    // Ruling 56 (Record 577): every listing query comes from src\data\disciplineQueries.js, shaped so the rules can
    // prove no officer query returns someone else's draft; the author's own cases (drafts included) are one more query.
    const qs = visibleCaseQueries(db, user);
    const sets = qs.map(() => []);
    let loaded = 0;
    const publish = () => {
      const byId = new Map();
      for (const s of sets) for (const row of s) byId.set(row.id, row);
      const rows = [...byId.values()];
      rows.sort((a, b) => ((b.openedAt && b.openedAt.toMillis ? b.openedAt.toMillis() : 0) - (a.openedAt && a.openedAt.toMillis ? a.openedAt.toMillis() : 0)));
      setCases(keepForViewer(user, rows));   // Record 613 (Bob 10/8), F22: an example is never in a real officer's list
    };
    if (!qs.length) { setCases([]); setLoading(false); return undefined; }
    const unsubs = qs.map((q, i) => onSnapshot(q, snap => {
      sets[i] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      publish();
      if (++loaded >= 1) { setLoadError(''); setLoading(false); }
    }, err => {
      console.error('Discipline list denied:', err);
      setLoadError(err && err.code ? err.code : 'error');
      setLoading(false);
    }));
    return () => unsubs.forEach(u => u());
  }, [user]);

  if (view === 'new') return (
    <NewDisciplineCase
      user={user}
      onSaved={(id) => { setSelectedCaseId(id); setView('detail'); }}
      onCancel={() => setView('list')}
    />
  );

  if (view === 'detail') return (
    <DisciplineCaseDetail
      caseId={selectedCaseId}
      user={user}
      onBack={() => setView('list')}
    />
  );

  const filtered = filter === 'All' ? cases : cases.filter(c => c.status === filter);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#F4F6F8' }}>⚖️ Discipline Cases</div>
          <div style={{ fontSize: 12, color: '#8AA4BF', marginTop: 2 }}>
            {cases.length} case{cases.length !== 1 ? 's' : ''} — separate from penalty time claims
          </div>
        </div>
        {mayOpen(user, 'new-case') && (   /* Record 592 (F24): a member reached New Case here past the screen gate (ruling 105) */
        <button className="btn btn-primary" onClick={() => setView('new')}>
          + Open Discipline Case
        </button>
        )}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['All', ...CASE_STAGES.map(s => s.key)].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: filter === f ? '#1A3A5C' : '#0A1628',
            color: filter === f ? '#4A9EE8' : 'var(--steel)',
            border: `1px solid ${filter === f ? '#2E4A6B' : '#1A2A3A'}`,
          }}>
            {f === 'All' ? 'All' : CASE_STAGES.find(s => s.key === f)?.label || f}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: '#8AA4BF', padding: 40 }}>Loading cases...</div>
      ) : loadError ? (
        <div style={{ textAlign: 'center', color: '#FF6B6B', padding: 40 }}>The case list could not be read ({loadError}). This is recorded; nothing was lost.</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--steel)', padding: 40 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚖️</div>
          <div style={{ fontSize: 14 }}>No discipline cases yet.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>When the carrier issues discipline, open a case here immediately.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0A1628' }}>
                {['Case #', 'Employee', 'Type', 'Committee', 'Status', 'Deadline', 'Opened'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => {
                const days = daysUntil(c.investigationDeadline);
                const urgentDeadline = days !== null && days <= 5 && c.status === 'notice_received';
                return (
                  <tr key={c.id}
                    style={{ background: i % 2 === 0 ? '#0D1B2A' : '#0A1628',
                      borderLeft: urgentDeadline ? '3px solid #C0392B' : '3px solid transparent' }}>
                    <td style={{ padding: '10px 12px' }}><button type="button"
                        onClick={() => { setSelectedCaseId(c.id); setView('detail'); }}
                        aria-label={'Open case ' + (c.caseNumber || 'with no number') + (c.memberName ? ' - ' + c.memberName : '')}
                        style={{ fontSize: 12, color: '#E8A020', fontFamily: 'monospace', fontWeight: 700, textDecoration: 'underline', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>{c.caseNumber}</button>{isExampleCase(c) ? <div style={{ fontFamily: 'inherit', fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>{EXAMPLE_MARK}</div> : null}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: '#F4F6F8' }}>{c.memberName}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#8AA4BF' }}>{c.disciplineType}{c.suspensionDays ? ` (${c.suspensionDays}d)` : ''}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: '#4A9EE8' }}>{c.generalCommittee}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12,
                        background: c.status === 'resolved' ? '#1A3A1A' : urgentDeadline ? '#2D0A0A' : '#1A2A3A',
                        color: c.status === 'resolved' ? '#40C070' : urgentDeadline ? '#FF6B6B' : '#8AA4BF' }}>
                        {CASE_STAGES.find(s => s.key === c.status)?.label || (c.status === 'gc_review' ? 'GC Review' : c.status === 'draft' ? 'Draft' : c.status)}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: urgentDeadline ? '#FF6B6B' : '#8AA4BF' }}>
                      {urgentDeadline ? `⚠ ${days}d` : formatDate(c.investigationDeadline)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--steel)' }}>
                      {c.openedAt?.toDate?.()?.toLocaleDateString() || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
